/**
 * MM4 Signal Handler
 * Processes place/edit/cancel signals from mm4.py with auto SL/TP placement
 * Includes EV+ Exit Algo for inventory management
 */

// ==================== EV+ EXIT PRESETS ====================
const EVPLUS_EXIT_PRESETS = {
    'FAST': {
        // Quick exits for volatile/illiquid markets
        range_base: 1.5,              // 1.5x ATR range before breakout triggers
        range_decay_interval: 30,     // Narrow range every 30s
        range_decay_rate: 0.75,       // Reduce by 0.75x ATR per interval
        range_min: 0.3,               // Minimum range 0.3x ATR
        infavor_wait: 30,             // Wait 30s before infavor decay
        infavor_decay_interval: 60,   // Decay infavor every 60s
        infavor_decay_rate: 0.15,     // Reduce by 0.15x ATR per interval
        infavor_min: -1.0,            // Minimum -1.0x ATR (becomes taker)
        unfavor_decay_interval: 30,   // Narrow unfavor every 30s
        unfavor_decay_rate: 0.5,      // Reduce by 0.5x ATR per interval
        unfavor_min: -1.0,            // Minimum -1.0x ATR (full taker)
        order_validity: 15,           // Re-quote every 15s
    },
    'DEFAULT': {
        // Balanced settings for normal conditions
        range_base: 3.0,
        range_decay_interval: 60,
        range_decay_rate: 0.5,
        range_min: 0.5,
        infavor_wait: 60,
        infavor_decay_interval: 60,   // Decay infavor every 60s
        infavor_decay_rate: 0.1,
        infavor_min: -1.0,
        unfavor_decay_interval: 30,
        unfavor_decay_rate: 0.3,
        unfavor_min: -1.0,
        order_validity: 30,
    },
    'SLOW': {
        // Patient exits for calm/liquid markets
        range_base: 4.5,
        range_decay_interval: 90,
        range_decay_rate: 0.3,
        range_min: 0.75,
        infavor_wait: 90,
        infavor_decay_interval: 90,   // Decay infavor every 90s (slower)
        infavor_decay_rate: 0.05,
        infavor_min: -1.0,
        unfavor_decay_interval: 30,
        unfavor_decay_rate: 0.2,
        unfavor_min: -1.0,
        order_validity: 45,
    },
};

class MM4SignalHandler {
    constructor() {
        this.hlClient = null;
        this.enabled = false;
        this.stats = {
            placed: 0,
            edited: 0,
            cancelled: 0,
            sltpPlaced: 0,
            errors: 0,
            rateLimitHits: 0,
            lastSignalTime: null,
            signalsPerMinute: 0
        };

        // Track orders with their SL/TP for when they fill
        this.activeOrders = new Map();    // request_id → [{oid, sl, tp, side, size, asset}]
        this.pendingSLTP = new Map();     // oid → {sl, tp, side, size, asset}
        this.filledPositions = new Map(); // asset → {side, size, sl, tp} - for SL/TP management

        // Duplicate fill protection: track processed fill IDs to prevent double-processing
        // fillId → timestamp (for TTL cleanup)
        this.processedFills = new Map();

        // === LOCAL OID TRACKING (like Python worker) ===
        // Track placed order OIDs locally to avoid API fetch on cancel
        // symbol → [{oid, side, price, size, timestamp}]
        this.trackedOrders = new Map();

        // === LEVEL-BASED ORDER TRACKING (for edit-first approach) ===
        // symbol → Map<levelKey, orderInfo>
        // levelKey = "buy_0", "buy_1", "sell_0", "sell_1", etc.
        // orderInfo = { oid, price, size, timestamp, sltp: {sl, tp} }
        this.ordersByLevel = new Map();

        // Threshold for quote refresh (only act if price changed > this amount)
        this.REFRESH_THRESHOLD_BPS = 10;

        // === MANAGED COINS (like Python worker's managed_coins) ===
        // Track which symbols we're actively trading (for optimized fill polling)
        // e.g., Set(['xyz:NVDA', 'vntl:SPACEX', 'BTC'])
        this.managedCoins = new Set();

        // Rate limiting
        this.lastRequestTime = 0;
        this.minRequestInterval = 5;      // 5ms between requests (200 req/sec burst, SDK handles actual rate limit)
        this.requestQueue = [];
        this.isProcessingQueue = false;

        // Cache for getAllUserOrders (avoid repeated calls)
        this.ordersCache = null;
        this.ordersCacheTime = 0;
        this.ordersCacheTTL = 2000;  // 2s cache (increased from 500ms to save API weight)

        // Cache for maxLeverage per symbol (avoid repeated getAssetMetadata calls)
        // symbol → { maxLeverage } - permanent cache until logout/refresh
        this.leverageCache = new Map();
        this.leverageCacheTTL = Infinity;  // Permanent cache (leverage rarely changes during session)

        // Signal rate tracking
        this.signalTimes = [];

        // Debug logging
        this.logBuffer = [];
        this.maxLogEntries = 1000;
        this.logLevel = 'debug';  // 'debug', 'info', 'warn', 'error'
        this.startTime = Date.now();

        // === NEW: Client-side configuration (for signal-only mode) ===
        // Each client manages their own inventory/budget independently
        this.clientConfig = this._loadClientConfig();

        // === NEW: Inventory state (per symbol) ===
        // symbol → { halted: bool, ratio: float, coins: float, maxCoins: float }
        this.inventoryState = new Map();

        // === NEW: Budget tracking (per symbol per side) ===
        // symbol → { buy: { target, order, position }, sell: { ... } }
        this.budgetState = new Map();

        // === NEW: Last known mid price per symbol (for size calculations) ===
        this.lastMidPrice = new Map();

        // === NEW: Flow data for UI display ===
        this.marketData = new Map();  // symbol → { mid, spread_bps, flow_score, toxicity, timestamp }

        // Auto-download settings
        this.autoDownloadEnabled = true;
        this.autoDownloadInterval = 5 * 60 * 1000;  // 5 minutes
        this.autoDownloadTimer = null;
        this.lastAutoDownload = 0;

        // === ORDER SYNC / RECONCILIATION (like backup's reconcile_budget) ===
        this.orderSyncInterval = null;
        this.orderSyncIntervalMs = 10000;  // 10 seconds
        this.orderGracePeriodMs = 5000;    // 5 second grace for recently placed orders

        // === SIGNAL SERIALIZATION (prevent race conditions) ===
        // Multiple signals can arrive simultaneously - serialize processing
        this.isProcessingSignal = false;
        this.signalQueue = [];

        // Flag to pause order sync during signal processing
        this.signalProcessingActive = false;

        // === FILL PAUSE (like backup's fill_requote_pause_sec) ===
        // symbol → timestamp when quote can resume
        this.fillPauseUntil = new Map();
        this.fillRequotePauseSec = 30;  // Match MM_FILL_REQUOTE_PAUSE_SEC from keys4.env

        // === EV+ EXIT ALGO STATE ===
        // symbol → { active: bool, phase: string, entryPrice: float, atr: float, haltTime: float, ... }
        this.exitAlgoState = new Map();

        // Exit algo preset (can be: 'AUTO', 'FAST', 'DEFAULT', 'SLOW')
        this.exitPreset = 'DEFAULT';

        // Track entry prices for positions (for exit algo calculations)
        // symbol → { entryPrice: float, side: string, size: float }
        this.positionEntries = new Map();

        // Track ATR per symbol (updated from market_data or quote_signal)
        // symbol → atr_value
        this.atrCache = new Map();

        // === INVENTORY SKEW (client-side calculation) ===
        // LONG → widen BID (discourage buying more)
        // SHORT → widen ASK (discourage selling more)
        this.inventorySkewEnabled = true;
        this.inventorySkewMaxMult = 2.0;      // Max spread multiplier at 100% inventory
        this.inventorySkewStartPct = 0.25;    // Start widening at 25% inventory

        // === DISTANCE/VELOCITY MONITORING ===
        this.distanceCheckEnabled = true;
        this.lastDistanceCancel = new Map();  // symbol → {buy: timestamp, sell: timestamp}
        this.velocityCheckEnabled = true;
        this.priceHistory = new Map();        // symbol → [{timestamp, price}, ...]
        this.lastVelocityCancel = new Map();  // symbol → {buy: timestamp, sell: timestamp}

        // Auto-download on page close
        this._setupAutoDownload();
    }

    /**
     * Setup auto-download on page close and periodic saves
     */
    _setupAutoDownload() {
        // Download logs when page is closed/refreshed
        window.addEventListener('beforeunload', () => {
            if (this.autoDownloadEnabled && this.logBuffer.length > 0) {
                this._saveLogsToDownloads();
            }
        });

        // Also try on visibilitychange (mobile/tab switch)
        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'hidden' && this.autoDownloadEnabled && this.logBuffer.length > 10) {
                this._saveLogsToDownloads();
            }
        });
    }

    /**
     * Start periodic auto-download (every 5 minutes)
     */
    startAutoDownload(intervalMinutes = 5) {
        this.autoDownloadEnabled = true;
        this.autoDownloadInterval = intervalMinutes * 60 * 1000;

        if (this.autoDownloadTimer) {
            clearInterval(this.autoDownloadTimer);
        }

        this.autoDownloadTimer = setInterval(() => {
            if (this.logBuffer.length > 0) {
                this._saveLogsToDownloads();
                this.log(`Auto-saved logs (${this.logBuffer.length} entries)`);
            }
        }, this.autoDownloadInterval);

        this.log(`Auto-download enabled: every ${intervalMinutes} minutes`);
    }

    /**
     * Stop periodic auto-download
     */
    stopAutoDownload() {
        this.autoDownloadEnabled = false;
        if (this.autoDownloadTimer) {
            clearInterval(this.autoDownloadTimer);
            this.autoDownloadTimer = null;
        }
        this.log('Auto-download disabled');
    }

    /**
     * Internal: Save logs to Downloads folder
     */
    _saveLogsToDownloads() {
        try {
            const content = this.getLogsText();
            if (!content) return;

            const now = new Date();
            const timestamp = now.toISOString().replace(/[:.]/g, '-').slice(0, 19);
            const filename = `mm4-log-${timestamp}.txt`;

            const blob = new Blob([content], { type: 'text/plain' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.style.display = 'none';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            this.lastAutoDownload = Date.now();
            // console.log(`[MM4] Auto-saved: ${filename}`);
        } catch (e) {
            // console.error('[MM4] Auto-save failed:', e);
        }
    }

    // ==================== LOGGING SYSTEM ====================

    /**
     * Log levels: debug < info < warn < error
     */
    _shouldLog(level) {
        const levels = { debug: 0, info: 1, warn: 2, error: 3 };
        return levels[level] >= levels[this.logLevel];
    }

    /**
     * Internal log method - stores in buffer and console
     */
    _log(level, message, data = null) {
        const timestamp = new Date().toISOString();
        const elapsed = ((Date.now() - this.startTime) / 1000).toFixed(2);

        const entry = {
            timestamp,
            elapsed: `+${elapsed}s`,
            level: level.toUpperCase(),
            message,
            data: data ? JSON.stringify(data) : null
        };

        // Add to buffer
        this.logBuffer.push(entry);
        if (this.logBuffer.length > this.maxLogEntries) {
            this.logBuffer.shift();  // Remove oldest
        }

        // Console output
        if (this._shouldLog(level)) {
            const prefix = `[MM4][${entry.elapsed}][${entry.level}]`;
            if (data) {
                console[level === 'debug' ? 'log' : level](`${prefix} ${message}`, data);
            } else {
                console[level === 'debug' ? 'log' : level](`${prefix} ${message}`);
            }
        }
    }

    log(message, data = null) { this._log('info', message, data); }
    debug(message, data = null) { this._log('debug', message, data); }
    warn(message, data = null) { this._log('warn', message, data); }
    error(message, data = null) { this._log('error', message, data); }

    /**
     * Get all logs as array
     */
    getLogs() {
        return [...this.logBuffer];
    }

    /**
     * Get logs as formatted string (like a log file)
     */
    getLogsText() {
        return this.logBuffer.map(e => {
            const dataStr = e.data ? ` | ${e.data}` : '';
            return `${e.timestamp} ${e.elapsed} [${e.level}] ${e.message}${dataStr}`;
        }).join('\n');
    }

    /**
     * Download logs as a file
     */
    downloadLogs() {
        const content = this.getLogsText();
        const filename = `mm4-chrome-worker-${new Date().toISOString().replace(/[:.]/g, '-')}.log`;

        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        this.log(`Logs downloaded: ${filename}`);
        return filename;
    }

    /**
     * Clear log buffer
     */
    clearLogs() {
        this.logBuffer = [];
        this.log('Log buffer cleared');
    }

    /**
     * Set log level
     */
    setLogLevel(level) {
        if (['debug', 'info', 'warn', 'error'].includes(level)) {
            this.logLevel = level;
            this.log(`Log level set to: ${level}`);
        }
    }

    /**
     * Track signal rate (signals per minute)
     */
    trackSignalRate() {
        const now = Date.now();
        this.signalTimes.push(now);
        // Keep only last 60 seconds
        this.signalTimes = this.signalTimes.filter(t => now - t < 60000);
        this.stats.signalsPerMinute = this.signalTimes.length;
    }

    /**
     * Rate-limited request wrapper
     */
    async rateLimitedRequest(fn) {
        const now = Date.now();
        const timeSinceLastRequest = now - this.lastRequestTime;

        if (timeSinceLastRequest < this.minRequestInterval) {
            const waitTime = this.minRequestInterval - timeSinceLastRequest;
            await new Promise(resolve => setTimeout(resolve, waitTime));
        }

        this.lastRequestTime = Date.now();
        return await fn();
    }

    /**
     * Get all user orders with caching
     * OPTIMIZED (like Python worker): Only queries DEXs with managed coins
     * - Uses getManagedDexs() to determine which DEXs to query
     * - e.g., ['xyz:NVDA', 'vntl:SPACEX'] → queries ['xyz', 'vntl']
     */
    async getCachedUserOrders(symbol = null) {
        const now = Date.now();
        if (this.ordersCache && (now - this.ordersCacheTime) < this.ordersCacheTTL) {
            return this.ordersCache;
        }

        // Determine which DEXs to query (like Python's managed_coins)
        let dexsToQuery = null;

        if (symbol) {
            // If specific symbol provided, query its DEX
            if (symbol.includes(':')) {
                dexsToQuery = [symbol.split(':')[0]];
            } else {
                dexsToQuery = [''];  // main DEX
            }
        } else {
            // Query all managed DEXs (like Python worker)
            const managedDexs = this.getManagedDexs();
            if (managedDexs.length > 0) {
                dexsToQuery = managedDexs;
            }
            // If no managed coins yet, dexsToQuery stays null (will query all DEXs as fallback)
        }

        this.ordersCache = await this.rateLimitedRequest(() =>
            this.hlClient.getAllUserOrders(dexsToQuery)
        );
        this.ordersCacheTime = now;
        return this.ordersCache;
    }

    /**
     * Invalidate orders cache
     */
    invalidateOrdersCache() {
        this.ordersCache = null;
        this.ordersCacheTime = 0;
    }

    /**
     * Initialize the signal handler
     * @param {Object} hlClient - BrowserHyperliquidClient instance
     */
    async initialize(hlClient) {
        // Guard against double initialization
        if (this.enabled) {
            this.log('Already initialized, skipping duplicate init');
            return this;
        }

        this.hlClient = hlClient || window.BrowserHyperliquidClient;

        if (!this.hlClient) {
            throw new Error('[MM4] HyperliquidClient not available');
        }

        // Listen for mm4 signals from Ably (only register once)
        if (window.AblyService) {
            window.AblyService.on('mm4-signal', (data) => {
                this.log('>>> SIGNAL FROM ABLY <<<', data);
                this.handleSignal(data);
            });
            this.log('Registered Ably signal listener');
            this.log(`AblyService status: connected=${window.AblyService.isConnected}, state=${window.AblyService.connectionState}`);
        } else {
            this.error('AblyService not available!');
        }

        // Subscribe to fills to place SL/TP when orders fill
        this.hlClient.onFill((fill) => this.handleFill(fill));

        this.enabled = true;
        this.log('Signal handler initialized with auto SL/TP');
        this.log(`Rate limit: ${this.minRequestInterval}ms between requests`);
        this.log(`Orders cache TTL: ${this.ordersCacheTTL}ms`);

        // Start auto-download (every 5 minutes)
        this.startAutoDownload(5);

        // Start periodic order sync (like backup's reconcile_budget)
        this._startOrderSync();

        return this;
    }

    /**
     * Start periodic order sync to reconcile trackedOrders with exchange
     */
    _startOrderSync() {
        if (this.orderSyncInterval) {
            clearInterval(this.orderSyncInterval);
        }

        this.orderSyncInterval = setInterval(async () => {
            try {
                await this._syncOrderTracking();
            } catch (e) {
                this.warn(`Order sync error: ${e.message}`);
            }
        }, this.orderSyncIntervalMs);

        this.log(`Order sync started (every ${this.orderSyncIntervalMs / 1000}s)`);
    }

    /**
     * Sync trackedOrders with actual exchange state (like backup's reconcile_budget + sync_order_tracking)
     * - Removes phantom orders (we think exist but don't on exchange)
     * - Detects stale orders (on exchange but not in tracking)
     */
    async _syncOrderTracking() {
        if (!this.hlClient || !this.enabled) return;

        // === FIX: Skip sync if signal is being processed (prevents race condition) ===
        if (this.signalProcessingActive) {
            this.debug('Order sync skipped - signal processing active');
            return;
        }

        const now = Date.now();
        let totalRemoved = 0;
        let totalStale = 0;

        for (const [symbol, tracked] of this.trackedOrders) {
            if (!tracked || tracked.length === 0) continue;

            // Check again inside loop in case signal started processing
            if (this.signalProcessingActive) {
                this.debug('Order sync interrupted - signal processing started');
                return;
            }

            try {
                // Fetch actual orders from exchange (fresh, no cache)
                this.ordersCache = null;  // Force fresh fetch
                const exchangeOrders = await this.hlClient.getAllUserOrders();

                // Filter for this symbol - handle ALL HIP-3 DEX prefixes (xyz:, vntl:, flx:, etc.)
                const symbolOrders = exchangeOrders.filter(o => {
                    const coin = o.coin || o.asset;
                    // Direct match
                    if (coin === symbol) return true;
                    // HIP-3: symbol has prefix (xyz:NVDA), coin is just asset (NVDA)
                    if (symbol.includes(':') && symbol.split(':')[1] === coin) return true;
                    // HIP-3: coin has prefix, symbol doesn't (unlikely but handle it)
                    if (coin.includes(':') && coin.split(':')[1] === symbol) return true;
                    // HIP-3: Both have same prefix format
                    if (coin.includes(':') && symbol.includes(':')) {
                        return coin.split(':')[1] === symbol.split(':')[1];
                    }
                    return false;
                });

                // Build set of exchange OIDs
                const exchangeOids = new Set(symbolOrders.map(o => String(o.oid || o.id)));

                // Find phantom orders (in tracking but not on exchange)
                const phantomOrders = [];
                const validOrders = [];

                for (const order of tracked) {
                    // Skip recently placed orders (grace period)
                    const age = now - (order.timestamp || 0);
                    if (age < this.orderGracePeriodMs) {
                        validOrders.push(order);
                        continue;
                    }

                    if (!exchangeOids.has(String(order.oid))) {
                        phantomOrders.push(order);
                    } else {
                        validOrders.push(order);
                    }
                }

                if (phantomOrders.length > 0) {
                    this.log(`${symbol} Removing ${phantomOrders.length} phantom orders (filled/cancelled)`);
                    this.trackedOrders.set(symbol, validOrders);
                    totalRemoved += phantomOrders.length;

                    // === FIX: Also clean up ordersByLevel for phantom orders ===
                    // This was missing - causing _shouldRefreshQuotes to return false
                    // because it saw stale entries in ordersByLevel
                    const levelMap = this.ordersByLevel.get(symbol);
                    if (levelMap) {
                        let levelsRemoved = 0;
                        for (const phantom of phantomOrders) {
                            for (const [levelKey, orderInfo] of levelMap) {
                                if (String(orderInfo.oid) === String(phantom.oid)) {
                                    levelMap.delete(levelKey);
                                    levelsRemoved++;
                                    this.debug(`Removed phantom OID ${phantom.oid} from level ${levelKey}`);
                                    break;
                                }
                            }
                        }
                        if (levelsRemoved > 0) {
                            this.log(`${symbol} Cleaned ${levelsRemoved} stale levels from ordersByLevel`);
                        }
                    }
                }

                // Find stale orders (on exchange but not in tracking)
                // NEVER cancel reduce-only orders - they're exit orders managed by Exit Algo
                const trackedOids = new Set(tracked.map(o => String(o.oid)));
                const staleOrders = symbolOrders.filter(o => {
                    const oid = o.oid || o.id;
                    // Skip reduce-only orders (exit orders) - they're managed separately
                    if (o.reduceOnly) {
                        this.debug(`${symbol} Skipping reduce-only order ${oid} in stale check`);
                        return false;
                    }
                    return !trackedOids.has(String(oid));
                });

                if (staleOrders.length > 0) {
                    this.warn(`${symbol} Found ${staleOrders.length} stale orders on exchange - cancelling`);
                    totalStale += staleOrders.length;

                    // Cancel stale orders
                    // NOTE: cancelMultipleOrders expects 'asset' and 'orderId', not 'coin' and 'oid'
                    const toCancel = staleOrders.map(o => ({
                        orderId: String(o.oid || o.id),
                        asset: symbol,
                        side: o.side || (o.b ? 'buy' : 'sell'),
                        reduceOnly: o.reduceOnly || false
                    }));

                    await this.rateLimitedRequest(() =>
                        this.hlClient.cancelMultipleOrders(toCancel)
                    );

                    this.log(`${symbol} Cancelled ${toCancel.length} stale orders`);
                }

            } catch (e) {
                this.warn(`${symbol} Order sync failed: ${e.message}`);
            }
        }

        if (totalRemoved > 0 || totalStale > 0) {
            this.log(`Order sync complete: removed=${totalRemoved} phantom, cancelled=${totalStale} stale`);
        }
    }

    /**
     * Main signal router - SERIALIZED to prevent race conditions
     * @param {Object} data - Signal data from mm4.py
     */
    async handleSignal(data) {
        if (!this.enabled || !this.hlClient) {
            this.warn('Handler not enabled or client not ready');
            return;
        }

        // === SIGNAL SERIALIZATION ===
        // Queue this signal and process serially to prevent race conditions
        if (this.isProcessingSignal) {
            // Already processing - queue this signal (will be deduped if same type+symbol)
            const existing = this.signalQueue.find(s => s.type === data.type && s.symbol === data.symbol);
            if (existing) {
                // Replace older signal of same type/symbol with newer one
                Object.assign(existing, data);
                this.debug(`Signal queued (updated): ${data.type} ${data.symbol}`);
            } else {
                this.signalQueue.push(data);
                this.debug(`Signal queued: ${data.type} ${data.symbol} (queue: ${this.signalQueue.length})`);
            }
            return;
        }

        this.isProcessingSignal = true;
        this.signalProcessingActive = true;

        try {
            await this._processSignal(data);

            // Process any queued signals
            while (this.signalQueue.length > 0) {
                const nextSignal = this.signalQueue.shift();
                await this._processSignal(nextSignal);
            }
        } finally {
            this.isProcessingSignal = false;
            this.signalProcessingActive = false;
        }
    }

    /**
     * Internal signal processor (called by handleSignal with serialization)
     * @param {Object} data - Signal data from mm4.py
     */
    async _processSignal(data) {
        const signalStart = Date.now();
        this.stats.lastSignalTime = signalStart;
        this.trackSignalRate();

        this.debug(`Signal processing: ${data.type}`, {
            symbol: data.symbol,
            orderCount: data.orders?.length,
            side: data.side,
            trigger: data.trigger || data.reason
        });

        try {
            switch (data.type) {
                case 'place_orders':
                    // Legacy format - still supported for backward compatibility
                    await this.rateLimitedRequest(() => this.handlePlaceOrders(data));
                    this.invalidateOrdersCache();  // Orders changed
                    break;
                case 'edit_orders':
                    await this.rateLimitedRequest(() => this.handleEditOrders(data));
                    this.invalidateOrdersCache();  // Cache invalidation was missing
                    break;
                case 'cancel':
                    // Legacy cancel format
                    await this.handleCancel(data);
                    this.invalidateOrdersCache();  // Orders changed
                    break;

                // === NEW SIGNAL TYPES (Signal-Only Mode) ===
                case 'quote_signal':
                    // New primary signal type: client checks inventory/budget then executes
                    await this.handleQuoteSignal(data);
                    this.invalidateOrdersCache();
                    break;
                case 'cancel_signal':
                    // New cancel format with urgency/reason
                    await this.handleCancelSignal(data);
                    this.invalidateOrdersCache();
                    break;
                case 'market_data':
                    // Market data for UI display (no execution)
                    this.handleMarketData(data);
                    break;

                default:
                    this.warn(`Unknown signal type: ${data.type}`);
            }

            const elapsed = Date.now() - signalStart;
            this.debug(`Signal processed in ${elapsed}ms: ${data.type}`);

        } catch (error) {
            const elapsed = Date.now() - signalStart;
            const errMsg = error?.message || error?.toString() || '';

            // Check for rate limit error
            if (errMsg.includes('rate') || errMsg.includes('429') || errMsg.includes('Too many')) {
                this.stats.rateLimitHits++;
                this.warn(`RATE LIMIT HIT (${this.stats.rateLimitHits} total) after ${elapsed}ms. Signals/min: ${this.stats.signalsPerMinute}`);
            } else {
                this.error(`Signal error after ${elapsed}ms: ${errMsg}`, { type: data.type, symbol: data.symbol });
            }
            this.stats.errors++;
        }
    }

    /**
     * Handle place_orders signal
     * Places orders and tracks SL/TP for when they fill
     *
     * @param {Object} data - {type, symbol, orders: [{side, price, base_amount, stop_loss, take_profit}], request_id}
     */
    async handlePlaceOrders(data) {
        this.log(`PLACE ${data.orders.length} orders for ${data.symbol}`, {
            orders: data.orders.map(o => ({ side: o.side, price: o.price, size: o.base_amount }))
        });

        // Format orders for batchPlace
        const orders = data.orders.map(o => ({
            asset: data.symbol,
            side: o.side,
            price: o.price,
            size: o.base_amount,
            reduceOnly: false
        }));

        try {
            const result = await this.hlClient.batchPlace(orders);

            // Track orders with their SL/TP for when they fill
            if (result?.response?.data?.statuses) {
                const orderData = [];

                result.response.data.statuses.forEach((status, i) => {
                    if (status.resting?.oid) {
                        const oid = status.resting.oid;
                        const originalOrder = data.orders[i];

                        // Validate SL/TP data from mm4.py
                        const hasSLTP = originalOrder.stop_loss && originalOrder.take_profit;

                        if (!hasSLTP) {
                            this.warn(`Order ${oid} missing SL/TP data from mm4.py`, {
                                stop_loss: originalOrder.stop_loss,
                                take_profit: originalOrder.take_profit,
                                rawOrder: originalOrder
                            });
                        }

                        // Store SL/TP to place when this order fills
                        this.pendingSLTP.set(oid, {
                            sl: originalOrder.stop_loss,
                            tp: originalOrder.take_profit,
                            side: originalOrder.side,
                            size: originalOrder.base_amount,
                            asset: data.symbol,
                            entryPrice: originalOrder.price
                        });

                        orderData.push({
                            oid,
                            side: originalOrder.side,
                            price: originalOrder.price,
                            size: originalOrder.base_amount,
                            sl: originalOrder.stop_loss,
                            tp: originalOrder.take_profit
                        });

                        this.log(`Order ${oid} placed, SL/TP pending: SL=${originalOrder.stop_loss || 'MISSING'}, TP=${originalOrder.take_profit || 'MISSING'}`);
                    } else if (status.filled) {
                        // Order filled immediately - place SL/TP now
                        const originalOrder = data.orders[i];
                        this.placeSLTPOrders(
                            data.symbol,
                            originalOrder.side,
                            originalOrder.base_amount,
                            originalOrder.stop_loss,
                            originalOrder.take_profit
                        );
                    }
                });

                if (data.request_id) {
                    this.activeOrders.set(data.request_id, orderData);
                }

                // === LOCAL OID TRACKING (like Python worker) ===
                // Save OIDs to trackedOrders so cancel doesn't need API fetch
                if (orderData.length > 0) {
                    const symbol = data.symbol;
                    if (!this.trackedOrders.has(symbol)) {
                        this.trackedOrders.set(symbol, []);
                    }
                    const tracked = this.trackedOrders.get(symbol);
                    for (const od of orderData) {
                        tracked.push({
                            oid: od.oid,
                            side: od.side,
                            price: od.price,
                            size: od.size,
                            timestamp: Date.now()
                        });
                    }

                    // Track this symbol as managed (like Python's managed_coins)
                    this.managedCoins.add(symbol);

                    this.debug(`Tracked ${orderData.length} OIDs for ${symbol}, total: ${tracked.length}, managedCoins: ${this.managedCoins.size}`);
                }
            }

            this.stats.placed += data.orders.length;
            this.log(`SUCCESS: Placed ${data.orders.length} orders for ${data.symbol}`);

        } catch (error) {
            this.error(`Place orders failed: ${error.message}`, { symbol: data.symbol });
            this.stats.errors++;
            throw error;
        }
    }

    /**
     * Handle order fill - cancel orders and place SL/TP
     * Like backup's fill handler: cancel filled side (or all on flip/large fill), then pause
     * @param {Object} fill - Fill event {oid, coin, side, sz, px}
     */
    async handleFill(fill) {
        const oid = fill.oid;
        this.debug(`Fill/Cancel event received for oid: ${oid}`, fill);

        // FIX Issue 1: Cancel events should NOT be processed as fills
        // Cancel events have type='cancel' and no fill.fill object
        if (fill.type === 'cancel') {
            this.debug(`Order ${oid} was cancelled, not a fill - skipping`);
            // Clean up pendingSLTP for cancelled orders
            if (this.pendingSLTP.has(oid)) {
                this.warn(`Order ${oid} cancelled - removing from pendingSLTP (SL/TP will NOT be placed)`);
                this.pendingSLTP.delete(oid);
            }
            return;
        }

        // Duplicate fill protection: generate unique fill ID from transaction data
        // FIX Issue 3: Include event type to prevent fill/cancel collision in same ms
        const fillId = fill.fill?.tid || `${oid}_${fill.type || 'fill'}_${fill.fill?.time || Date.now()}`;
        if (this.processedFills.has(fillId)) {
            this.debug(`Fill ${fillId} already processed, skipping duplicate`);
            return;
        }

        // Mark as processed with timestamp for TTL cleanup
        this.processedFills.set(fillId, Date.now());

        // Cleanup old processed fills (older than 60 seconds)
        const cleanupThreshold = Date.now() - 60000;
        for (const [id, timestamp] of this.processedFills) {
            if (timestamp < cleanupThreshold) {
                this.processedFills.delete(id);
            }
        }

        const sltpData = this.pendingSLTP.get(oid);

        if (!sltpData) {
            // Not an mm4 order, already processed, or was cancelled
            this.debug(`Order ${oid} not in pendingSLTP (cancelled or not MM4 order)`);
            return;
        }

        const symbol = sltpData.asset;
        const fillSide = sltpData.side;  // 'buy' or 'sell'

        // FIX: Use ACTUAL fill size from event, not original order size (for partial fills)
        // fill.fill contains the raw fill data with sz, px, coin, side
        // FIX Issue 2: Validate parseFloat result to prevent NaN corruption
        let actualFillSize = sltpData.size;  // Default to order size
        if (fill.fill?.sz) {
            const parsedSize = parseFloat(fill.fill.sz);
            if (Number.isFinite(parsedSize) && parsedSize > 0) {
                actualFillSize = parsedSize;
            } else {
                this.warn(`Order ${oid} has invalid fill size: ${fill.fill.sz}, using order size`);
            }
        }
        const orderSize = sltpData.size;

        // Track cumulative fills for partial fill handling
        if (!sltpData.filledSize) {
            sltpData.filledSize = 0;
        }
        sltpData.filledSize += actualFillSize;

        const isFullyFilled = sltpData.filledSize >= orderSize - 0.0000001;  // Epsilon for float precision
        const fillSize = actualFillSize;  // Use actual fill size for all operations

        // Validate SL/TP values
        if (!sltpData.sl || !sltpData.tp) {
            this.warn(`Order ${oid} filled but SL/TP values missing`, sltpData);
            if (isFullyFilled) {
                this.pendingSLTP.delete(oid);
            }
            return;
        }

        this.log(`🔔 FILL detected: Order ${oid} ${fillSide.toUpperCase()} ${fillSize.toFixed(4)}/${orderSize.toFixed(4)} (${isFullyFilled ? 'FULL' : 'PARTIAL'}) @ $${sltpData.entryPrice?.toFixed(5) || '?'}`, {
            asset: symbol,
            side: fillSide,
            fillSize: fillSize,
            totalFilled: sltpData.filledSize,
            orderSize: orderSize,
            sl: sltpData.sl,
            tp: sltpData.tp
        });

        try {
            // === FILL-DETECTED CANCEL (like backup's fill handler) ===
            // Determine if we need to cancel ALL orders or just filled side
            let cancelAll = false;
            let cancelReason = 'fill';

            // Check for position flip or large fill
            // NOTE: Position tracker may have stale data (race condition)
            // We calculate previous position from fill data, not current position
            let position = null;
            let isFlip = false;  // Track flip state for budget handling later
            let currentNet = 0;  // Track current position for budget calculation after flip
            try {
                if (window.BrowserPositionTracker) {
                    position = window.BrowserPositionTracker.positions.get(symbol);
                }
            } catch (e) {
                this.warn(`Failed to get position for fill cancel: ${e.message}`);
            }

            if (position && position.size !== undefined) {
                // Calculate previousNet from fill data (avoids race condition)
                // Fill data tells us: side (buy/sell) and size
                // If we filled a BUY, position increased by fillSize
                // If we filled a SELL, position decreased by fillSize
                currentNet = position.size;
                const previousNet = fillSide === 'buy'
                    ? currentNet - fillSize  // was smaller before buy fill
                    : currentNet + fillSize; // was larger before sell fill

                // Condition 1: Position flip - cancel all and reset budget
                isFlip = (previousNet > 0 && currentNet < 0) || (previousNet < 0 && currentNet > 0);
                if (isFlip) {
                    cancelAll = true;
                    cancelReason = 'fill_position_flip';
                    this.warn(`⚠️ ${symbol} POSITION FLIP detected (${previousNet.toFixed(4)} → ${currentNet.toFixed(4)}) - cancelling ALL orders`);
                    // Reset budget on flip - old position closed, start fresh for new direction
                    this._resetBudget(symbol);
                }

                // Condition 2: Large fill (>50% of max inventory) - cancel all
                // Check price availability first (avoid silent failure with Infinity)
                if (!cancelAll) {
                    const midPrice = this.lastMidPrice.get(symbol);
                    if (!midPrice || midPrice <= 0) {
                        // Price unknown - log warning, default to safe behavior (cancel filled side only)
                        this.warn(`${symbol} Price unknown - cannot calculate large fill threshold, using default cancel`);
                    } else {
                        const maxInventory = this._getMaxInventoryCoins(symbol);
                        if (maxInventory > 0 && maxInventory !== Infinity) {
                            const fillRatio = fillSize / maxInventory;
                            if (fillRatio >= 0.5) {
                                cancelAll = true;
                                cancelReason = 'fill_large';
                                this.warn(`⚠️ ${symbol} LARGE FILL detected (${(fillRatio * 100).toFixed(0)}% of max_inv=${maxInventory.toFixed(4)}) - cancelling ALL orders`);
                            }
                        }
                    }
                }
            } else {
                this.warn(`${symbol} Position data unavailable - defaulting to cancel filled side only`);
            }

            // Execute cancel
            if (cancelAll) {
                await this.handleCancel({ type: 'cancel', symbol, side: 'all', reason: cancelReason });
                this.log(`${symbol} fill flow: cancelled ALL orders due to ${cancelReason}`);
            } else {
                // Normal case: cancel filled side only, keep opposite
                await this.handleCancel({ type: 'cancel', symbol, side: fillSide, reason: 'fill' });
                this.log(`${symbol} fill flow: cancelled ${fillSide.toUpperCase()} orders, kept opposite side`);
            }

            // === PAUSE QUOTING (like backup's fill_requote_pause_sec) ===
            const pauseUntil = Date.now() + (this.fillRequotePauseSec * 1000);
            this.fillPauseUntil.set(symbol, pauseUntil);
            this.log(`${symbol} quote paused for ${this.fillRequotePauseSec}s after fill (until ${new Date(pauseUntil).toISOString()})`);

            // === PLACE SL/TP ===
            // FIX: Only place SL/TP when FULLY filled (avoid multiple SL/TP orders for partial fills)
            if (isFullyFilled) {
                await this.placeSLTPOrders(
                    symbol,
                    fillSide,
                    orderSize,  // Use total order size, not partial fill size
                    sltpData.sl,
                    sltpData.tp
                );
            } else {
                this.debug(`${symbol} Partial fill - deferring SL/TP placement until fully filled`);
            }

            // Update budget tracking (move from order to position)
            // Use entryPrice from sltpData, fallback to lastMidPrice, skip if both unavailable
            let entryPrice = sltpData.entryPrice;
            if (!entryPrice || !Number.isFinite(entryPrice) || entryPrice <= 0) {
                entryPrice = this.lastMidPrice.get(symbol);
                if (!entryPrice || !Number.isFinite(entryPrice) || entryPrice <= 0) {
                    // Skip budget update - using fallback=1 would corrupt tracking
                    this.warn(`${symbol} No valid entry price - skipping budget tracking for this fill`);
                    entryPrice = null;
                } else {
                    this.debug(`${symbol} Using lastMidPrice ($${entryPrice.toFixed(5)}) for budget tracking`);
                }
            }
            if (entryPrice) {
                if (isFlip) {
                    // On flip, budget was reset - set NEW position value (not full flip fill)
                    // currentNet (from outer scope) is the new position after flip
                    if (currentNet === 0) {
                        this.log(`Flip resulted in flat position - budget reset, no position to track`);
                    } else {
                        const newPositionUsd = Math.abs(currentNet) * entryPrice;
                        const newSide = currentNet > 0 ? 'buy' : 'sell';
                        const budget = this.budgetState.get(symbol);
                        if (budget) {
                            budget[newSide].position = newPositionUsd;
                            this.log(`Budget set for new ${newSide} position: $${newPositionUsd.toFixed(0)} after flip`);
                        }
                    }
                } else {
                    // Normal fill - add to position budget
                    const filledSizeUsd = fillSize * entryPrice;
                    this._updateBudgetOnFill(symbol, fillSide, filledSizeUsd);
                }
            }

            // FIX: Only remove from pending when FULLY filled (for partial fill support)
            if (isFullyFilled) {
                this.pendingSLTP.delete(oid);
                this.debug(`Order ${oid} fully filled, removed from pendingSLTP`);
            } else {
                this.debug(`Order ${oid} partially filled (${sltpData.filledSize}/${orderSize}), keeping in pendingSLTP`);
            }

            // FIX: Only cleanup tracking when FULLY filled (partial fills keep order on book)
            if (isFullyFilled) {
                // === CLEANUP: Remove filled order from trackedOrders ===
                const tracked = this.trackedOrders.get(symbol);
                if (tracked) {
                    const remaining = tracked.filter(o => o.oid !== oid);
                    this.trackedOrders.set(symbol, remaining);
                    this.debug(`Removed filled OID ${oid} from tracking, ${remaining.length} remaining`);
                }

                // === CLEANUP: Remove filled order from ordersByLevel ===
                const levelMap = this.ordersByLevel.get(symbol);
                if (levelMap) {
                    for (const [levelKey, orderInfo] of levelMap) {
                        if (orderInfo.oid === oid) {
                            levelMap.delete(levelKey);
                            this.debug(`Removed filled OID ${oid} from level ${levelKey}`);
                            break;
                        }
                    }
                }
            } else {
                // Partial fill: update remaining size in ordersByLevel
                const levelMap = this.ordersByLevel.get(symbol);
                if (levelMap) {
                    for (const [levelKey, orderInfo] of levelMap) {
                        if (orderInfo.oid === oid) {
                            // FIX: Prevent negative size from race conditions
                            orderInfo.size = Math.max(0, orderInfo.size - fillSize);
                            this.debug(`Partial fill: Updated OID ${oid} size to ${orderInfo.size.toFixed(4)} in level ${levelKey}`);
                            break;
                        }
                    }
                }
            }

            // Track filled position for SL/TP management (accumulate for partial fills)
            const positionKey = `${symbol}_${fillSide}`;
            const existingFilled = this.filledPositions.get(positionKey);
            // FIX: Always accumulate fill size, even on full fill (don't overwrite accumulated total)
            if (existingFilled) {
                existingFilled.size += fillSize;
                existingFilled.filledAt = Date.now();
                if (isFullyFilled) {
                    existingFilled.complete = true;
                }
            } else {
                this.filledPositions.set(positionKey, {
                    side: fillSide,
                    size: fillSize,
                    sl: sltpData.sl,
                    tp: sltpData.tp,
                    entryPrice: sltpData.entryPrice,
                    filledAt: Date.now(),
                    complete: isFullyFilled
                });
            }

            this.log(`✅ SUCCESS: ${isFullyFilled ? 'FULL' : 'PARTIAL'} fill handled for order ${oid} - cancelled ${cancelAll ? 'ALL' : fillSide} orders, ${isFullyFilled ? 'SL/TP placed' : 'SL/TP deferred'}, quote paused ${this.fillRequotePauseSec}s`);

        } catch (error) {
            this.error(`Fill handler failed for order ${oid}: ${error.message}`);
            this.stats.errors++;
        }
    }

    /**
     * Place SL and TP orders for a filled position
     * @param {string} asset - Asset symbol
     * @param {string} entrySide - Original entry side (buy/sell)
     * @param {number} size - Position size
     * @param {number} slPrice - Stop loss trigger price
     * @param {number} tpPrice - Take profit trigger price
     */
    async placeSLTPOrders(asset, entrySide, size, slPrice, tpPrice) {
        // SL and TP are on opposite side of entry
        const exitSide = entrySide === 'buy' ? 'sell' : 'buy';

        try {
            // Place Stop Loss order
            const slOrder = {
                asset: asset,
                side: exitSide,
                triggerPrice: slPrice,
                size: size,
                reduceOnly: true
            };

            await this.hlClient.placeStopOrder(slOrder);
            this.debug(`SL placed @ ${slPrice} for ${asset}`);

            // Place Take Profit order
            const tpOrder = {
                asset: asset,
                side: exitSide,
                triggerPrice: tpPrice,
                size: size,
                reduceOnly: true
            };

            await this.hlClient.placeTakeProfitOrder(tpOrder);
            this.debug(`TP placed @ ${tpPrice} for ${asset}`);

            this.stats.sltpPlaced++;
            this.log(`SL/TP placed: SL @ ${slPrice}, TP @ ${tpPrice} for ${asset} (${entrySide})`);

        } catch (error) {
            this.error(`Failed to place SL/TP for ${asset}: ${error.message}`);
            throw error;
        }
    }

    /**
     * Handle edit_orders signal
     * Modifies existing orders (price/size changes)
     *
     * @param {Object} data - {type, symbol, orders: [{oid, side, new_price, new_size}], trigger}
     */
    async handleEditOrders(data) {
        this.log(`EDIT ${data.orders.length} orders for ${data.symbol}`, {
            trigger: data.trigger,
            orders: data.orders.map(o => ({ oid: o.oid, price: o.new_price, size: o.new_size }))
        });

        // Format for batchModify
        const modifies = data.orders.map(o => ({
            oid: o.oid,
            asset: data.symbol,
            side: o.side,
            price: o.new_price,
            size: o.new_size,
            reduceOnly: false
        }));

        try {
            await this.hlClient.batchModify(modifies);
            this.stats.edited += data.orders.length;
            this.log(`SUCCESS: Edited ${data.orders.length} orders (${data.trigger})`);
        } catch (error) {
            this.error(`Edit orders failed: ${error.message}`, { symbol: data.symbol, trigger: data.trigger });
            this.stats.errors++;

            // Clear cache on failure to prevent stale data
            this.ordersCache = null;
            this.ordersCacheTime = 0;
            throw error;
        }
    }

    /**
     * Handle cancel signal
     * Cancels orders by symbol and side
     * Uses LOCAL tracked OIDs first (no API call), falls back to API if needed
     *
     * @param {Object} data - {type, symbol, side, reason}
     */
    async handleCancel(data) {
        this.log(`CANCEL ${data.side} orders for ${data.symbol}`, { reason: data.reason });

        try {
            let toCancel = [];
            let usedTracked = false;

            // === USE LOCAL TRACKED ORDERS ONLY (like Python worker) ===
            // Do NOT fall back to API - stale API data causes duplicate cancels
            const tracked = this.trackedOrders.get(data.symbol);
            if (tracked && tracked.length > 0) {
                // Filter by side
                const sideFilter = data.side === 'all' ? null : data.side.toLowerCase();
                toCancel = tracked
                    .filter(o => !o.isExitOrder && (!sideFilter || o.side.toLowerCase() === sideFilter))
                    .map(o => ({
                        orderId: String(o.oid),
                        asset: data.symbol,
                        side: o.side,
                        reduceOnly: false
                    }));

                if (toCancel.length > 0) {
                    usedTracked = true;
                    this.debug(`Using ${toCancel.length} TRACKED orders for cancel`);
                }
            } else {
                this.debug(`No tracked orders for ${data.symbol} - nothing to cancel`);
            }

            if (toCancel.length > 0) {
                // Cancel the orders (skip rate limit for immediate urgency)
                if (data.urgency === 'immediate') {
                    await this.hlClient.cancelMultipleOrders(toCancel);
                } else {
                    await this.rateLimitedRequest(() =>
                        this.hlClient.cancelMultipleOrders(toCancel)
                    );
                }

                // Remove pending SL/TP for cancelled orders
                toCancel.forEach(o => {
                    const oid = o.orderId;
                    if (this.pendingSLTP.has(oid)) {
                        this.pendingSLTP.delete(oid);
                        this.debug(`Removed pending SL/TP for cancelled order ${oid}`);
                    }
                });

                // === CLEANUP: Remove cancelled OIDs from trackedOrders ===
                // Note: orderId is String, but tracked.oid might be number - use String() for safe comparison
                const cancelledOids = new Set(toCancel.map(o => o.orderId));
                if (usedTracked && tracked) {
                    const remaining = tracked.filter(o => !cancelledOids.has(String(o.oid)));
                    this.trackedOrders.set(data.symbol, remaining);
                    this.debug(`Removed ${cancelledOids.size} cancelled OIDs, ${remaining.length} remaining`);
                } else {
                    // If we used API fetch, clear trackedOrders for this symbol entirely
                    // (we don't know which orders are still valid)
                    this.trackedOrders.set(data.symbol, []);
                }

                // === CLEANUP: Remove from ordersByLevel ===
                const levelMap = this.ordersByLevel.get(data.symbol);
                if (levelMap) {
                    const sideFilter = data.side === 'all' ? null : data.side.toLowerCase();
                    for (const [levelKey, orderInfo] of levelMap) {
                        if (!sideFilter || levelKey.startsWith(sideFilter)) {
                            if (cancelledOids.has(String(orderInfo.oid))) {
                                levelMap.delete(levelKey);
                            }
                        }
                    }
                    this.debug(`Cleaned ordersByLevel for ${data.symbol}: ${levelMap.size} levels remaining`);
                }

                // === CLEAR API CACHE after cancel to prevent stale data ===
                this.ordersCache = null;
                this.ordersCacheTime = 0;

                this.stats.cancelled += toCancel.length;
                this.log(`SUCCESS: Cancelled ${toCancel.length} orders (${data.reason}) [${usedTracked ? 'TRACKED' : 'API'}]`, {
                    symbol: data.symbol,
                    side: data.side,
                    oids: toCancel.map(o => o.orderId)
                });
            } else {
                this.debug(`No orders to cancel for ${data.symbol} ${data.side}`);
            }
        } catch (error) {
            this.error(`Cancel orders failed: ${error.message}`, { symbol: data.symbol, reason: data.reason });
            this.stats.errors++;
            throw error;
        }
    }

    // ==================== NEW SIGNAL HANDLERS (Signal-Only Mode) ====================

    /**
     * Handle quote_signal - NEW primary signal type for signal-only mode
     * Client checks own inventory/budget, then executes orders
     *
     * @param {Object} data - {type, symbol, mid_price, atr, bids, asks, bid_mult, ask_mult, trigger, timestamp}
     *   bids/asks: [{price, size_usd, level, stop_loss, take_profit}]
     */
    async handleQuoteSignal(data) {
        const symbol = data.symbol;
        const midPrice = data.mid_price;

        // Update last known mid price
        this.lastMidPrice.set(symbol, midPrice);

        // Cache ATR for exit algo calculations
        if (data.atr && data.atr > 0) {
            this.atrCache.set(symbol, data.atr);
        }

        // Update market data for UI (preserve dynamic thresholds from market_data signal)
        const existingMarketData = this.marketData.get(symbol) || {};
        this.marketData.set(symbol, {
            ...existingMarketData,  // Preserve thresholds from market_data signal
            mid: midPrice,
            atr: data.atr || existingMarketData.atr || 0,
            bid_mult: data.bid_mult || 1.0,
            ask_mult: data.ask_mult || 1.0,
            exit_preset: data.exit_preset || 'DEFAULT',
            timestamp: data.timestamp || Date.now() / 1000
        });

        // Update exit preset from server (for exit algo to use)
        if (data.exit_preset) {
            this.exitPreset = data.exit_preset;
        }

        // CLIENT-SIDE PRICE MONITORING: Check distance/velocity BEFORE processing
        // This proactively cancels orders at risk before they get picked off
        const cancelled = await this._checkPriceAndCancelIfNeeded(symbol, midPrice);
        if (cancelled) {
            this.debug(`${symbol} Orders cancelled by price monitoring - continuing with quote signal`);
        }

        // Step 0: Check if fill pause is active (like backup's fill_requote_pause_sec)
        const fillPauseUntil = this.fillPauseUntil.get(symbol);
        if (fillPauseUntil && Date.now() < fillPauseUntil) {
            const remainingSec = ((fillPauseUntil - Date.now()) / 1000).toFixed(1);
            this.debug(`${symbol} Quote paused after fill (${remainingSec}s remaining) - skipping quote signal`);
            return;
        } else if (fillPauseUntil && Date.now() >= fillPauseUntil) {
            // Pause expired, clear it
            this.fillPauseUntil.delete(symbol);
            this.log(`${symbol} Fill pause expired - resuming quotes`);
        }

        // Step 1: Check if exit algo is active - if so, skip normal quoting
        const exitState = this.exitAlgoState.get(symbol);
        if (exitState?.active) {
            // Check for stale/stuck exit algo (stuck in initializing for >30s, or running >35 min)
            const startTime = exitState.haltTime || exitState.startTime;
            if (!startTime) {
                this.error(`Exit algo missing timestamps for ${symbol} - force cleanup`);
                this._stopExitAlgo(symbol, false);
                // Continue processing signal normally
            }
            const exitElapsed = startTime ? (Date.now() - startTime) : 0;
            const maxDuration = exitState.maxDuration || 30 * 60 * 1000;

            if (!startTime) {
                // Already cleaned up above, continue processing
            } else if (exitState.phase === 'initializing' && exitElapsed > 30000) {
                this.error(`Exit algo STUCK in initializing for ${symbol} (${Math.floor(exitElapsed/1000)}s) - force cleanup`);
                this._stopExitAlgo(symbol, false);
                // Continue to process this signal normally
            } else if (exitElapsed > maxDuration + 5 * 60 * 1000) {
                // 5 min grace period beyond max duration
                this.error(`Exit algo EXPIRED for ${symbol} (${Math.floor(exitElapsed/60000)} min) - force cleanup`);
                this._stopExitAlgo(symbol, false);
                // Continue to process this signal normally
            } else {
                this.debug(`Exit algo active for ${symbol} (phase: ${exitState.phase}, ${Math.floor(exitElapsed/1000)}s) - skipping quote signal`);
                return;
            }
        }

        // Step 2: Check inventory halt
        const inventoryCheck = await this._checkInventoryHalt(symbol);
        if (inventoryCheck.halted) {
            this.log(`🛑 INVENTORY HALT: ${symbol} at ${(inventoryCheck.ratio * 100).toFixed(1)}% - starting Exit Algo`);
            // Cancel all orders when halted (reduce risk)
            await this.handleCancel({ type: 'cancel', symbol, side: 'all', reason: 'inventory_halt' });

            // Start Exit Algo instead of just returning
            this._startExitAlgo(symbol, inventoryCheck);
            return;
        }

        // === EDIT-FIRST APPROACH ===
        // Build target orders and check if refresh needed
        const targetOrders = this._buildTargetOrders(symbol, data.bids, data.asks);

        if (targetOrders.size === 0) {
            this.debug(`No target orders for ${symbol} (budget exhausted or all filtered)`);
            return;
        }

        // Check threshold - skip if prices haven't changed significantly
        if (!this._shouldRefreshQuotes(symbol, targetOrders)) {
            this.debug(`Quotes unchanged for ${symbol} (within ${this.REFRESH_THRESHOLD_BPS}bps threshold) - skipping`);
            return;
        }

        // Step 5: Check actual account margin before placing orders
        // This prevents "Insufficient margin" errors from the exchange
        try {
            if (!window.BrowserPositionTracker) {
                this.error(`No BrowserPositionTracker - cannot verify margin, skipping orders for ${symbol}`);
                return;
            }

            const accountSummary = window.BrowserPositionTracker.accountSummary;
            if (!accountSummary || accountSummary.accountValue === undefined) {
                this.error(`No accountSummary data - cannot verify margin, skipping orders for ${symbol}`);
                return;
            }

            const accountValue = accountSummary.accountValue || 0;
            const crossMarginUsed = accountSummary.marginUsed || 0;

            // Sum isolated margin from all HIP-3 positions
            // accountSummary.marginUsed only includes cross margin (main DEX)
            // HIP-3 isolated margins are per-position and need to be summed manually
            let isolatedMarginUsed = 0;
            for (const pos of window.BrowserPositionTracker.positions.values()) {
                if (pos.isHip3 || pos.leverage?.type === 'isolated') {
                    isolatedMarginUsed += pos.marginUsed || 0;
                }
            }

            // Total margin = cross + isolated
            const totalMarginUsed = crossMarginUsed + isolatedMarginUsed;
            const availableMargin = accountValue - totalMarginUsed;
            const marginType = 'total';

            this.debug(`${symbol} Margin: accountValue=$${accountValue.toFixed(2)}, crossUsed=$${crossMarginUsed.toFixed(2)}, isolatedUsed=$${isolatedMarginUsed.toFixed(2)}, available=$${availableMargin.toFixed(2)}`)

            // Calculate total order value from target orders
            let totalOrderValue = 0;
            for (const [, order] of targetOrders) {
                totalOrderValue += order.size * order.price;
            }

            // Get actual maxLeverage from Hyperliquid for this symbol (cached, fallback to 3x)
            let maxLeverage = 3;  // Conservative fallback
            const cached = this.leverageCache.get(symbol);
            const now = Date.now();

            if (cached && (now - cached.cachedAt) < this.leverageCacheTTL) {
                // Use cached value
                maxLeverage = cached.maxLeverage;
            } else {
                // Fetch from API and cache
                try {
                    const assetMeta = await this.hlClient.getAssetMetadata();
                    const asset = assetMeta?.universe?.find(a => a.name === symbol);
                    if (asset?.maxLeverage && asset.maxLeverage > 0) {
                        maxLeverage = asset.maxLeverage;
                        this.leverageCache.set(symbol, { maxLeverage, cachedAt: now });
                    }
                } catch (e) {
                    this.debug(`Could not fetch maxLeverage for ${symbol}, using fallback ${maxLeverage}x`);
                }
            }

            // Required margin = orderValue / leverage (with 20% safety buffer)
            const requiredMargin = (totalOrderValue / maxLeverage) * 1.2;

            this.debug(`Margin check [${marginType}]: available=$${availableMargin.toFixed(2)}, required=$${requiredMargin.toFixed(2)}, orderValue=$${totalOrderValue.toFixed(2)}, leverage=${maxLeverage}x`);

            if (availableMargin <= 0) {
                this.error(`NO MARGIN AVAILABLE ($${availableMargin.toFixed(2)}) - skipping ALL orders for ${symbol}`);
                return;
            }

            if (availableMargin < requiredMargin) {
                this.warn(`Insufficient margin for ${symbol}: available=$${availableMargin.toFixed(2)}, required=$${requiredMargin.toFixed(2)}`);

                const scaleFactor = Math.max(0, availableMargin / requiredMargin);
                if (scaleFactor < 0.2) {
                    this.warn(`Margin too low (${(scaleFactor * 100).toFixed(0)}%) - skipping quote signal for ${symbol}`);
                    return;
                }
                // Note: scaling is handled inside handleQuoteSignalWithEdit via budget
                this.log(`Margin constraint: will scale to ${(scaleFactor * 100).toFixed(0)}%`);
            }
        } catch (e) {
            this.error(`Margin check failed for ${symbol}: ${e.message} - NOT placing orders`);
            return;
        }

        // Step 6: Use edit-first approach for quote management
        try {
            await this.handleQuoteSignalWithEdit(data);
        } catch (error) {
            this.error(`Quote signal execution failed: ${error.message}`, { symbol, trigger: data.trigger });
            this.stats.errors++;
            throw error;
        }
    }

    /**
     * Handle cancel_signal - NEW cancel format with urgency/reason
     *
     * @param {Object} data - {type, symbol, side, reason, urgency, timestamp}
     */
    async handleCancelSignal(data) {
        const urgency = data.urgency || 'normal';
        const reason = data.reason || 'unknown';

        this.log(`🚫 CANCEL_SIGNAL: ${data.symbol} ${data.side} (${reason}, ${urgency})`);

        // Pass urgency to handleCancel so it can skip rate limiting for immediate urgency
        await this.handleCancel({
            type: 'cancel',
            symbol: data.symbol,
            side: data.side,
            reason: reason,
            urgency: urgency
        });
    }

    /**
     * Handle market_data - Optional broadcast for UI display
     *
     * @param {Object} data - {type, symbol, mid_price, spread_bps, atr, flow_score, toxicity, timestamp}
     */
    handleMarketData(data) {
        this.marketData.set(data.symbol, {
            mid: data.mid_price,
            spread_bps: data.spread_bps,
            atr: data.atr || 0,
            tick_size: data.tick_size || 0.01,
            flow_score: data.flow_score || 0,
            toxicity: data.toxicity || 0,

            // Dynamic thresholds from mm4 (for distance/velocity monitoring)
            distance_threshold_bps: data.distance_threshold_bps || 5,
            velocity_threshold_bps_sec: data.velocity_threshold_bps_sec || 10,

            // Orderbook depth info
            bid_depth_usd: data.bid_depth_usd || 0,
            ask_depth_usd: data.ask_depth_usd || 0,
            imbalance: data.imbalance || 0.5,
            regime_volatility: data.regime_volatility || 'normal',

            // Market hours (for HIP-3 weighting)
            is_market_hours: data.is_market_hours || false,

            timestamp: data.timestamp || Date.now() / 1000
        });

        this.debug(`Market data update: ${data.symbol} mid=$${data.mid_price?.toFixed(4)}, spread=${data.spread_bps?.toFixed(1)}bps, dist_th=${data.distance_threshold_bps?.toFixed(1)}bps, vel_th=${data.velocity_threshold_bps_sec?.toFixed(1)}bps/s`);
    }

    // ==================== EDIT-FIRST QUOTE MANAGEMENT ====================

    /**
     * Build target orders Map from signal bids/asks
     * @param {string} symbol - Trading symbol
     * @param {Array} bids - Bid orders from signal [{price, size_usd, level, stop_loss, take_profit}]
     * @param {Array} asks - Ask orders from signal
     * @returns {Map} levelKey → {price, size, side, level, sltp}
     */
    _buildTargetOrders(symbol, bids, asks) {
        const targets = new Map();
        const midPrice = this.lastMidPrice.get(symbol);

        // Guard against invalid/missing mid price - skip quoting if no valid reference
        if (!midPrice || !Number.isFinite(midPrice) || midPrice <= 0) {
            this.warn(`${symbol} No valid mid price (got ${midPrice}) - skipping quote build`);
            return targets;  // Return empty Map
        }

        // Get budgets
        const buyBudget = this._getAvailableBudget(symbol, 'buy');
        const sellBudget = this._getAvailableBudget(symbol, 'sell');

        // Get inventory skew multipliers (CLIENT-SIDE calculation)
        // LONG → bidMult > 1 → lower effective bid (less aggressive buying)
        // SHORT → askMult > 1 → higher effective ask (less aggressive selling)
        const skew = this._calculateInventorySkew(symbol);

        // Get server's flow-based multipliers (from quote_signal)
        const marketData = this.marketData.get(symbol) || {};
        const serverBidMult = marketData.bid_mult || 1.0;
        const serverAskMult = marketData.ask_mult || 1.0;

        // Combined multipliers: server (flow toxicity) × client (inventory skew)
        const combinedBidMult = serverBidMult * skew.bidMult;
        const combinedAskMult = serverAskMult * skew.askMult;

        // Process bids (apply combined multiplier to SPREAD: widen bid spread)
        let usedBuyBudget = 0;
        for (const bid of (bids || [])) {
            if (usedBuyBudget >= buyBudget) break;

            const scaledSizeUsd = this._scaleOrderSize(bid.size_usd, symbol);
            const availableBudget = buyBudget - usedBuyBudget;
            const finalSizeUsd = Math.min(scaledSizeUsd, availableBudget);

            if (finalSizeUsd < 10) continue;

            // Validate bid price
            if (!bid.price || !Number.isFinite(bid.price) || bid.price <= 0) continue;

            // Apply combined multiplier to SPREAD from mid price
            // spread = midPrice - bid.price, adjustedSpread = spread * combinedBidMult
            // adjustedPrice = midPrice - adjustedSpread
            // Clamp spread to non-negative (bid above mid = stale data, use 0 spread)
            const bidSpread = Math.max(0, midPrice - bid.price);
            const adjustedPrice = midPrice - (bidSpread * combinedBidMult);

            // Guard against invalid adjusted price (extreme skew could push price negative)
            if (adjustedPrice <= 0) continue;

            const size = finalSizeUsd / adjustedPrice;
            const levelKey = `buy_${bid.level || 0}`;
            targets.set(levelKey, {
                price: adjustedPrice,
                size: size,
                side: 'buy',
                level: bid.level || 0,
                sltp: { sl: bid.stop_loss, tp: bid.take_profit }
            });
            usedBuyBudget += finalSizeUsd;
        }

        // Process asks (apply combined multiplier to SPREAD: widen ask spread)
        let usedSellBudget = 0;
        for (const ask of (asks || [])) {
            if (usedSellBudget >= sellBudget) break;

            const scaledSizeUsd = this._scaleOrderSize(ask.size_usd, symbol);
            const availableBudget = sellBudget - usedSellBudget;
            const finalSizeUsd = Math.min(scaledSizeUsd, availableBudget);

            if (finalSizeUsd < 10) continue;

            // Validate ask price
            if (!ask.price || !Number.isFinite(ask.price) || ask.price <= 0) continue;

            // Apply combined multiplier to SPREAD from mid price
            // spread = ask.price - midPrice, adjustedSpread = spread * combinedAskMult
            // adjustedPrice = midPrice + adjustedSpread
            // Clamp spread to non-negative (ask below mid = stale data, use 0 spread)
            const askSpread = Math.max(0, ask.price - midPrice);
            const adjustedPrice = midPrice + (askSpread * combinedAskMult);

            // Guard against invalid adjusted price
            if (adjustedPrice <= 0) continue;

            const size = finalSizeUsd / adjustedPrice;
            const levelKey = `sell_${ask.level || 0}`;
            targets.set(levelKey, {
                price: adjustedPrice,
                size: size,
                side: 'sell',
                level: ask.level || 0,
                sltp: { sl: ask.stop_loss, tp: ask.take_profit }
            });
            usedSellBudget += finalSizeUsd;
        }

        return targets;
    }

    /**
     * Check if price changed beyond threshold
     * @param {number} oldPrice - Current order price
     * @param {number} newPrice - Target price
     * @returns {boolean} True if price changed significantly
     */
    _priceChanged(oldPrice, newPrice) {
        if (!oldPrice || !newPrice) return true;
        const deltaBps = Math.abs(newPrice - oldPrice) / oldPrice * 10000;
        return deltaBps > this.REFRESH_THRESHOLD_BPS;
    }

    /**
     * Check if quotes should be refreshed (threshold check)
     * @param {string} symbol - Trading symbol
     * @param {Map} targetOrders - Target orders from signal
     * @returns {boolean} True if refresh needed
     */
    _shouldRefreshQuotes(symbol, targetOrders) {
        const currentOrders = this.ordersByLevel.get(symbol);
        if (!currentOrders || currentOrders.size === 0) return true;  // No orders, definitely refresh

        // Check if any price changed beyond threshold
        for (const [levelKey, target] of targetOrders) {
            const existing = currentOrders.get(levelKey);
            if (!existing) return true;  // New level needed

            if (this._priceChanged(existing.price, target.price)) {
                return true;  // Price changed significantly
            }
        }

        // Check if any existing levels should be removed
        for (const [levelKey] of currentOrders) {
            if (!targetOrders.has(levelKey)) {
                return true;  // Level needs to be removed
            }
        }

        return false;  // All prices within threshold
    }

    /**
     * Batch edit orders using hlClient.batchModify
     * @param {Array} edits - [{oid, levelKey, newPrice, newSize, side}]
     * @param {string} symbol - Trading symbol
     */
    async _batchEditOrders(edits, symbol) {
        if (edits.length === 0) return { success: 0, failed: 0 };

        try {
            const modifies = edits.map(e => ({
                oid: e.oid,
                asset: symbol,
                side: e.side,
                price: e.newPrice,
                size: e.newSize,
                reduceOnly: false
            }));

            const result = await this.rateLimitedRequest(() =>
                this.hlClient.batchModify(modifies)
            );

            // Track successes/failures from response
            let successCount = 0;
            let failedCount = 0;
            const failedEdits = [];

            if (result?.response?.data?.statuses) {
                result.response.data.statuses.forEach((status, i) => {
                    if (status.error) {
                        failedCount++;
                        failedEdits.push(edits[i]);
                        this.warn(`Edit failed for ${edits[i].levelKey}: ${status.error}`);
                    } else {
                        successCount++;
                        const e = edits[i];

                        // Update ordersByLevel ONLY on success
                        const levelMap = this.ordersByLevel.get(symbol);
                        if (levelMap) {
                            const orderInfo = levelMap.get(e.levelKey);
                            if (orderInfo) {
                                orderInfo.price = e.newPrice;
                                orderInfo.size = e.newSize;
                            }
                        }

                        // Also update trackedOrders for consistency
                        const tracked = this.trackedOrders.get(symbol);
                        if (tracked) {
                            const trackedOrder = tracked.find(o => o.oid === e.oid);
                            if (trackedOrder) {
                                trackedOrder.price = e.newPrice;
                                trackedOrder.size = e.newSize;
                            }
                        }
                    }
                });
            } else {
                // No detailed status - assume all succeeded
                successCount = edits.length;
                const levelMap = this.ordersByLevel.get(symbol);
                const tracked = this.trackedOrders.get(symbol);
                for (const e of edits) {
                    if (levelMap) {
                        const orderInfo = levelMap.get(e.levelKey);
                        if (orderInfo) {
                            orderInfo.price = e.newPrice;
                            orderInfo.size = e.newSize;
                        }
                    }
                    if (tracked) {
                        const trackedOrder = tracked.find(o => o.oid === e.oid);
                        if (trackedOrder) {
                            trackedOrder.price = e.newPrice;
                            trackedOrder.size = e.newSize;
                        }
                    }
                }
            }

            this.stats.edited += successCount;
            return { success: successCount, failed: failedCount, failedEdits };

        } catch (error) {
            this.error(`Batch edit failed: ${error.message}`);
            this.stats.errors++;

            // Clear cache on failure to prevent stale data
            this.ordersCache = null;
            this.ordersCacheTime = 0;
            return { success: 0, failed: edits.length, failedEdits: edits };
        }
    }

    /**
     * Batch place new orders
     * @param {Array} places - [{levelKey, price, size, side, level, sltp}]
     * @param {string} symbol - Trading symbol
     */
    async _batchPlaceNewOrders(places, symbol) {
        if (places.length === 0) return { success: 0, failed: 0 };

        try {
            const ordersToPlace = places.map(p => ({
                asset: symbol,
                side: p.side,
                price: p.price,
                size: p.size,
                reduceOnly: false
            }));

            const result = await this.rateLimitedRequest(() =>
                this.hlClient.batchPlace(ordersToPlace)
            );

            // === FIX: Track partial success - check each status individually ===
            let successCount = 0;
            let failedCount = 0;

            if (result?.response?.data?.statuses) {
                result.response.data.statuses.forEach((status, i) => {
                    const placeInfo = places[i];

                    // Check for error in this specific order
                    if (status.error) {
                        failedCount++;
                        this.warn(`Order ${i} failed for ${symbol} ${placeInfo.side}: ${status.error}`);
                        return;
                    }

                    // Handle resting order (normal limit order)
                    if (status.resting?.oid) {
                        const oid = status.resting.oid;
                        successCount++;

                        // Store SL/TP for when order fills
                        this.pendingSLTP.set(oid, {
                            sl: placeInfo.sltp?.sl,
                            tp: placeInfo.sltp?.tp,
                            side: placeInfo.side,
                            size: placeInfo.size,
                            asset: symbol,
                            entryPrice: placeInfo.price
                        });

                        // Track in legacy orders
                        if (!this.trackedOrders.has(symbol)) {
                            this.trackedOrders.set(symbol, []);
                        }
                        this.trackedOrders.get(symbol).push({
                            oid,
                            side: placeInfo.side,
                            price: placeInfo.price,
                            size: placeInfo.size,
                            timestamp: Date.now()
                        });

                        // Track by level
                        if (!this.ordersByLevel.has(symbol)) {
                            this.ordersByLevel.set(symbol, new Map());
                        }
                        this.ordersByLevel.get(symbol).set(placeInfo.levelKey, {
                            oid,
                            price: placeInfo.price,
                            size: placeInfo.size,
                            timestamp: Date.now(),
                            sltp: placeInfo.sltp
                        });
                    }
                    // Handle immediate fill (order filled instantly, no resting)
                    else if (status.filled) {
                        successCount++;
                        this.debug(`Order ${i} for ${symbol} ${placeInfo.side} filled immediately`);
                        // Note: Fill callback will handle SL/TP placement
                        // Don't track in ordersByLevel since it's already filled
                    }
                    // Unknown status - count as failed
                    else {
                        failedCount++;
                        this.warn(`Order ${i} for ${symbol} ${placeInfo.side}: unknown status`, status);
                    }
                });
            } else {
                // No statuses returned - all failed
                failedCount = places.length;
                this.warn(`batchPlace returned no statuses for ${symbol}`);
            }

            this.stats.placed += successCount;
            if (failedCount > 0) {
                this.warn(`Batch place partial: ${successCount} success, ${failedCount} failed for ${symbol}`);
            }
            return { success: successCount, failed: failedCount };

        } catch (error) {
            this.error(`Batch place failed: ${error.message}`);
            this.stats.errors++;
            return { success: 0, failed: places.length };
        }
    }

    /**
     * Batch cancel orders by level
     * @param {Array} cancels - [{oid, levelKey}]
     * @param {string} symbol - Trading symbol
     */
    async _batchCancelByLevel(cancels, symbol) {
        if (cancels.length === 0) return { success: 0, failed: 0 };

        try {
            // NOTE: cancelMultipleOrders expects 'asset' and 'orderId', not 'coin' and 'oid'
            const toCancel = cancels.map(c => ({
                orderId: String(c.oid),
                asset: symbol,
                side: c.levelKey.split('_')[0],
                reduceOnly: false
            }));

            await this.rateLimitedRequest(() =>
                this.hlClient.cancelMultipleOrders(toCancel)
            );

            // Remove from tracking
            const cancelledOids = new Set(cancels.map(c => String(c.oid)));

            // Legacy tracking
            const tracked = this.trackedOrders.get(symbol);
            if (tracked) {
                const remaining = tracked.filter(o => !cancelledOids.has(String(o.oid)));
                this.trackedOrders.set(symbol, remaining);
            }

            // Level tracking
            const levelMap = this.ordersByLevel.get(symbol);
            if (levelMap) {
                for (const c of cancels) {
                    const orderInfo = levelMap.get(c.levelKey);
                    if (orderInfo && String(orderInfo.oid) === String(c.oid)) {
                        levelMap.delete(c.levelKey);
                    }
                }
            }

            // Remove pending SL/TP
            for (const c of cancels) {
                this.pendingSLTP.delete(c.oid);
            }


            // Clear API cache after cancel
            this.ordersCache = null;
            this.ordersCacheTime = 0;
            this.stats.cancelled += cancels.length;
            return { success: cancels.length, failed: 0 };

        } catch (error) {
            this.error(`Batch cancel failed: ${error.message}`);
            this.stats.errors++;
            return { success: 0, failed: cancels.length };
        }
    }

    /**
     * Handle quote signal with edit-first approach
     * Instead of cancel-all + place-all, we:
     * 1. Compare target vs current by level
     * 2. EDIT orders where price changed
     * 3. PLACE new levels
     * 4. CANCEL removed levels
     *
     * @param {Object} data - Quote signal data
     */
    async handleQuoteSignalWithEdit(data) {
        const symbol = data.symbol;

        // Build target orders from signal
        const targetOrders = this._buildTargetOrders(symbol, data.bids, data.asks);

        if (targetOrders.size === 0) {
            this.debug(`No target orders for ${symbol} (budget exhausted)`);
            return;
        }

        // Get current orders
        const currentOrders = this.ordersByLevel.get(symbol) || new Map();

        const edits = [];
        const places = [];
        const cancels = [];

        // Compare each target level
        for (const [levelKey, target] of targetOrders) {
            const current = currentOrders.get(levelKey);

            if (!current) {
                // Level missing → place new order
                places.push({ levelKey, ...target });
            } else if (this._priceChanged(current.price, target.price)) {
                // Price changed → edit order
                edits.push({
                    oid: current.oid,
                    levelKey,
                    newPrice: target.price,
                    newSize: target.size,
                    side: target.side
                });
            }
            // else: price same → do nothing
        }

        // Find levels to remove
        for (const [levelKey, current] of currentOrders) {
            if (!targetOrders.has(levelKey)) {
                cancels.push({ oid: current.oid, levelKey });
            }
        }

        // Log action summary
        this.debug(`Edit-first for ${symbol}: ${edits.length} edits, ${places.length} places, ${cancels.length} cancels`);

        // Execute in order: edits first (keep OIDs), then places, then cancels
        if (edits.length > 0) {
            const editResult = await this._batchEditOrders(edits, symbol);
            if (editResult.failed > 0 && editResult.failedEdits) {
                // Fallback: cancel ONLY failed edits and re-place them
                this.warn(`${editResult.failed} edits failed - falling back to cancel+place for failed orders`);
                for (const e of editResult.failedEdits) {
                    cancels.push({ oid: e.oid, levelKey: e.levelKey });
                    const target = targetOrders.get(e.levelKey);
                    if (target) {
                        places.push({ levelKey: e.levelKey, ...target });
                    }
                }
            }
        }

        if (places.length > 0) {
            await this._batchPlaceNewOrders(places, symbol);
        }

        if (cancels.length > 0) {
            await this._batchCancelByLevel(cancels, symbol);
        }

        const buyCount = [...targetOrders.values()].filter(o => o.side === 'buy').length;
        const sellCount = [...targetOrders.values()].filter(o => o.side === 'sell').length;
        this.log(`✅ EDIT-FIRST: ${symbol} ${edits.length}E/${places.length}P/${cancels.length}C → ${buyCount} bids, ${sellCount} asks`);
    }

    // ==================== CLIENT-SIDE INVENTORY & BUDGET MANAGEMENT ====================

    /**
     * Load client configuration from localStorage
     * @returns {Object} - Client config
     */
    _loadClientConfig() {
        try {
            const stored = localStorage.getItem('mm4_client_config');
            if (stored) {
                const config = JSON.parse(stored);
                // console.log('[MM4] Loaded client config:', config);
                return config;
            }
        } catch (e) {
            // console.warn('[MM4] Failed to load client config:', e);
        }

        // Default config
        return {
            positionValue: 1000,          // USD per side (total = 2x this)
            haltThreshold: 0.90,          // 90% inventory halt
            alertThreshold: 0.75,         // 75% inventory alert
            warningThreshold: 0.50,       // 50% inventory warning
            enabled: true                 // Enable signal processing
        };
    }

    /**
     * Save client configuration to localStorage
     */
    _saveClientConfig() {
        try {
            localStorage.setItem('mm4_client_config', JSON.stringify(this.clientConfig));
            // console.log('[MM4] Saved client config');
        } catch (e) {
            // console.warn('[MM4] Failed to save client config:', e);
        }
    }

    /**
     * Update client configuration
     * @param {Object} newConfig - Partial config to merge
     */
    updateClientConfig(newConfig) {
        this.clientConfig = { ...this.clientConfig, ...newConfig };
        this._saveClientConfig();
        this.log(`Client config updated: ${JSON.stringify(newConfig)}`);
    }

    /**
     * Check if inventory halt threshold is reached
     * @param {string} symbol - Trading symbol
     * @returns {Object} - { halted: bool, ratio: float, coins: float, maxCoins: float }
     */
    async _checkInventoryHalt(symbol) {
        // Try to get position from BrowserPositionTracker
        let position = null;
        try {
            if (window.BrowserPositionTracker) {
                position = window.BrowserPositionTracker.positions.get(symbol);
            }
        } catch (e) {
            this.warn(`Failed to get position for ${symbol}: ${e.message}`);
        }

        // Dust threshold: positions smaller than this are treated as flat
        const DUST_THRESHOLD_COINS = 0.0001;
        const positionSize = position?.size || 0;

        if (!position || Math.abs(positionSize) < DUST_THRESHOLD_COINS) {
            // No position or dust = not halted
            // SYNC: Reset budget when position is flat (fixes stale budget from old fills)
            const budget = this.budgetState.get(symbol);
            if (budget && (budget.buy.position > 1 || budget.sell.position > 1)) {
                this.log(`${symbol} Position is FLAT but budget shows fills (buy=$${budget.buy.position.toFixed(0)}, sell=$${budget.sell.position.toFixed(0)}) - resetting`);
                this._resetBudget(symbol);
            }
            return { halted: false, ratio: 0, coins: 0, maxCoins: this._getMaxInventoryCoins(symbol) };
        }

        const coins = Math.abs(position.size);
        const maxCoins = this._getMaxInventoryCoins(symbol);
        const ratio = maxCoins > 0 ? coins / maxCoins : 0;

        const halted = ratio >= this.clientConfig.haltThreshold;

        // SYNC: Ensure budget matches actual position direction
        const midPrice = this.lastMidPrice.get(symbol);
        if (midPrice && midPrice > 0) {
            const actualPositionUsd = coins * midPrice;
            const isLong = position.size > 0;
            const budget = this.budgetState.get(symbol);

            if (budget) {
                const expectedSide = isLong ? 'buy' : 'sell';
                const wrongSide = isLong ? 'sell' : 'buy';

                // Reset if budget is on wrong side OR both sides have significant budget (stale)
                const wrongSideHasBudget = budget[wrongSide].position > 10;
                const bothSidesHaveBudget = budget.buy.position > 10 && budget.sell.position > 10;

                if (wrongSideHasBudget || bothSidesHaveBudget) {
                    this.log(`${symbol} Budget mismatch: buy=$${budget.buy.position.toFixed(0)}, sell=$${budget.sell.position.toFixed(0)}, actual=${isLong ? 'LONG' : 'SHORT'} $${actualPositionUsd.toFixed(0)} - syncing`);
                    const newBudget = this._resetBudget(symbol);  // Returns the new budget atomically
                    newBudget[expectedSide].position = actualPositionUsd;
                }
            }
        } else {
            this.debug(`${symbol} Skipping budget sync - no valid midPrice`);
        }

        // Update inventory state
        this.inventoryState.set(symbol, { halted, ratio, coins, maxCoins });

        return { halted, ratio, coins, maxCoins };
    }

    /**
     * Calculate max inventory coins based on position value and current price
     * @param {string} symbol - Trading symbol
     * @returns {number} - Max inventory in coins
     */
    _getMaxInventoryCoins(symbol) {
        const midPrice = this.lastMidPrice.get(symbol);
        // Return Infinity if price unknown/invalid (fail-safe - no inventory limit)
        if (!midPrice || !Number.isFinite(midPrice) || midPrice <= 0) {
            return Infinity;
        }
        const positionValue = this.clientConfig.positionValue;
        if (!positionValue || positionValue <= 0) {
            return Infinity;
        }
        return positionValue / midPrice;
    }

    /**
     * Calculate inventory-based spread skew (CLIENT-SIDE)
     * LONG → widen BID (discourage buying more)
     * SHORT → widen ASK (discourage selling more)
     * @param {string} symbol - Trading symbol
     * @returns {Object} {bidMult, askMult} - Multipliers for bid/ask prices
     */
    _calculateInventorySkew(symbol) {
        if (!this.inventorySkewEnabled) {
            return { bidMult: 1.0, askMult: 1.0 };
        }

        const invState = this.inventoryState.get(symbol);
        if (!invState || invState.ratio < this.inventorySkewStartPct) {
            return { bidMult: 1.0, askMult: 1.0 };
        }

        // Get position direction from BrowserPositionTracker
        let position = null;
        try {
            if (window.BrowserPositionTracker) {
                position = window.BrowserPositionTracker.positions.get(symbol);
            }
        } catch (e) {
            return { bidMult: 1.0, askMult: 1.0 };
        }

        if (!position || !position.size) {
            return { bidMult: 1.0, askMult: 1.0 };
        }

        const isLong = position.size > 0;
        const ratio = invState.ratio;

        // Guard against division by zero if inventorySkewStartPct >= 1.0
        const denominator = 1.0 - this.inventorySkewStartPct;
        if (denominator <= 0) {
            return { bidMult: 1.0, askMult: 1.0 };
        }

        // Quadratic ramp from start_pct to 100%
        const effectiveRatio = Math.min(1.0, (ratio - this.inventorySkewStartPct) / denominator);
        const skewMult = 1.0 + (this.inventorySkewMaxMult - 1.0) * Math.pow(effectiveRatio, 2);

        if (isLong) {
            // LONG → widen BID to discourage more buying
            this.debug(`${symbol} SKEW: LONG ${(ratio * 100).toFixed(1)}% → bidMult=${skewMult.toFixed(2)}x`);
            return { bidMult: skewMult, askMult: 1.0 };
        } else {
            // SHORT → widen ASK to discourage more selling
            this.debug(`${symbol} SKEW: SHORT ${(ratio * 100).toFixed(1)}% → askMult=${skewMult.toFixed(2)}x`);
            return { bidMult: 1.0, askMult: skewMult };
        }
    }

    // ==================== CLIENT-SIDE PRICE MONITORING ====================

    /**
     * Check if price is approaching orders dangerously close (CLIENT-SIDE).
     * Uses dynamic threshold from mm4's market_data signal.
     * @param {string} symbol - Trading symbol
     * @param {number} midPrice - Current mid price
     * @returns {Set<string>} - Set of sides to cancel ('buy'/'sell') - empty if none
     */
    _checkPriceDistance(symbol, midPrice) {
        const sidesAtRisk = new Set();
        if (!this.distanceCheckEnabled) return sidesAtRisk;

        const levelMap = this.ordersByLevel.get(symbol);
        if (!levelMap || levelMap.size === 0) return sidesAtRisk;

        // Get dynamic threshold from market_data (from mm4)
        const marketData = this.marketData.get(symbol) || {};
        const threshold = marketData.distance_threshold_bps || 5;

        // Cooldown to prevent rapid-fire cancels
        const now = Date.now();
        const cooldown = this.lastDistanceCancel.get(symbol) || {};
        const lastMid = this._priceHistoryLast(symbol);

        for (const [levelKey, orderInfo] of levelMap) {
            const side = levelKey.startsWith('buy') ? 'buy' : 'sell';
            if (sidesAtRisk.has(side)) continue;  // Already marked this side

            const orderPrice = orderInfo.price;

            // Skip if recently cancelled this side (reduced from 2000ms to 1000ms)
            if (cooldown[side] && (now - cooldown[side]) < 1000) continue;

            const distanceBps = Math.abs(midPrice - orderPrice) / midPrice * 10000;

            // CRITICAL: Price has crossed our order - immediate cancel
            if (side === 'buy' && midPrice <= orderPrice) {
                this.log(`⚠️ ${symbol} DISTANCE: Price ${midPrice.toFixed(4)} CROSSED buy order at ${orderPrice.toFixed(4)} - cancel`);
                sidesAtRisk.add(side);
                continue;
            }
            if (side === 'sell' && midPrice >= orderPrice) {
                this.log(`⚠️ ${symbol} DISTANCE: Price ${midPrice.toFixed(4)} CROSSED sell order at ${orderPrice.toFixed(4)} - cancel`);
                sidesAtRisk.add(side);
                continue;
            }

            // EMERGENCY ZONE: < 2bps - cancel regardless of direction
            // This catches cases where price oscillates near the order
            if (distanceBps < 2) {
                this.log(`⚠️ ${symbol} DISTANCE: CRITICAL ${distanceBps.toFixed(1)}bps from ${side} at ${orderPrice.toFixed(4)} - cancel`);
                sidesAtRisk.add(side);
                continue;
            }

            // Price approaching within dynamic threshold + moving towards order
            if (distanceBps <= threshold && lastMid) {
                // Only cancel if price is MOVING TOWARDS the order
                if (side === 'buy' && midPrice < lastMid) {
                    this.log(`⚠️ ${symbol} DISTANCE: Price ${midPrice.toFixed(4)} approaching buy at ${orderPrice.toFixed(4)} (${distanceBps.toFixed(1)}bps < ${threshold.toFixed(1)}bps)`);
                    sidesAtRisk.add(side);
                }
                if (side === 'sell' && midPrice > lastMid) {
                    this.log(`⚠️ ${symbol} DISTANCE: Price ${midPrice.toFixed(4)} approaching sell at ${orderPrice.toFixed(4)} (${distanceBps.toFixed(1)}bps < ${threshold.toFixed(1)}bps)`);
                    sidesAtRisk.add(side);
                }
            }
        }

        // Update cooldowns for sides we're cancelling
        if (sidesAtRisk.size > 0) {
            const newCooldown = { ...cooldown };
            for (const side of sidesAtRisk) {
                newCooldown[side] = now;
            }
            this.lastDistanceCancel.set(symbol, newCooldown);
        }

        return sidesAtRisk;
    }

    /**
     * Check if price velocity is dangerous (approaching orders too fast).
     * Uses dynamic threshold from mm4's market_data signal.
     * @param {string} symbol - Trading symbol
     * @param {number} midPrice - Current mid price
     * @param {number} timestamp - Current timestamp (ms)
     * @returns {Set<string>} - Set of sides to cancel ('buy'/'sell') - empty if none
     */
    _checkPriceVelocity(symbol, midPrice, timestamp) {
        const sidesAtRisk = new Set();
        if (!this.velocityCheckEnabled) return sidesAtRisk;

        // Update price history
        this._updatePriceHistory(symbol, midPrice, timestamp);

        // Need at least 2 data points
        const history = this.priceHistory.get(symbol) || [];
        if (history.length < 2) return sidesAtRisk;

        // Get velocity over last 3 seconds
        const lookbackMs = 3000;
        const cutoff = timestamp - lookbackMs;
        const recent = history.filter(h => h.timestamp >= cutoff);
        if (recent.length < 2) return sidesAtRisk;

        // Guard against invalid midPrice to prevent division by zero
        if (!midPrice || !Number.isFinite(midPrice) || midPrice <= 0) {
            return sidesAtRisk;
        }

        const oldest = recent[0];
        const newest = recent[recent.length - 1];
        const timeDiffSec = (newest.timestamp - oldest.timestamp) / 1000;
        if (timeDiffSec < 0.5) return sidesAtRisk;  // Need at least 500ms for stable velocity

        const priceDiff = newest.price - oldest.price;
        const velocityBpsPerSec = (priceDiff / midPrice * 10000) / timeDiffSec;

        // Get dynamic threshold from market_data (from mm4)
        const marketData = this.marketData.get(symbol) || {};
        const threshold = marketData.velocity_threshold_bps_sec || 10;

        // Cooldown to prevent rapid-fire cancels
        const now = Date.now();
        const cooldown = this.lastVelocityCancel.get(symbol) || {};

        const levelMap = this.ordersByLevel.get(symbol);
        if (!levelMap || levelMap.size === 0) return sidesAtRisk;

        // Positive velocity = price going UP
        // Negative velocity = price going DOWN
        if (Math.abs(velocityBpsPerSec) > threshold) {
            for (const [levelKey, orderInfo] of levelMap) {
                const side = levelKey.startsWith('buy') ? 'buy' : 'sell';
                if (sidesAtRisk.has(side)) continue;  // Already marked

                const orderPrice = orderInfo.price;

                // Skip if recently cancelled this side
                if (cooldown[side] && (now - cooldown[side]) < 2000) continue;

                const distanceBps = Math.abs(midPrice - orderPrice) / midPrice * 10000;
                const timeToReachSec = distanceBps / Math.abs(velocityBpsPerSec);

                // If order will be reached within 1 second at current velocity
                if (timeToReachSec < 1.0) {
                    // Price going DOWN fast → threatens BUY orders
                    if (side === 'buy' && velocityBpsPerSec < -threshold) {
                        this.log(`⚠️ ${symbol} VELOCITY: Price dropping ${Math.abs(velocityBpsPerSec).toFixed(1)}bps/s → buy at ${orderPrice.toFixed(4)} in ${timeToReachSec.toFixed(2)}s`);
                        sidesAtRisk.add(side);
                    }
                    // Price going UP fast → threatens SELL orders
                    if (side === 'sell' && velocityBpsPerSec > threshold) {
                        this.log(`⚠️ ${symbol} VELOCITY: Price rising ${velocityBpsPerSec.toFixed(1)}bps/s → sell at ${orderPrice.toFixed(4)} in ${timeToReachSec.toFixed(2)}s`);
                        sidesAtRisk.add(side);
                    }
                }
            }
        }

        // Update cooldowns for sides we're cancelling
        if (sidesAtRisk.size > 0) {
            const newCooldown = { ...cooldown };
            for (const side of sidesAtRisk) {
                newCooldown[side] = now;
            }
            this.lastVelocityCancel.set(symbol, newCooldown);
        }

        return sidesAtRisk;
    }

    /**
     * Update price history for velocity calculation
     */
    _updatePriceHistory(symbol, price, timestamp) {
        if (!this.priceHistory.has(symbol)) {
            this.priceHistory.set(symbol, []);
        }
        const history = this.priceHistory.get(symbol);
        history.push({ price, timestamp });

        // Hard cap to prevent memory leak (max 100 entries)
        const maxLength = 100;
        if (history.length > maxLength) {
            history.splice(0, history.length - maxLength);
        }

        // Also prune by time (keep last 5 seconds) using splice for efficiency
        const cutoff = timestamp - 5000;
        let removeCount = 0;
        for (let i = 0; i < history.length; i++) {
            if (history[i].timestamp < cutoff) {
                removeCount++;
            } else {
                break;
            }
        }
        if (removeCount > 0) {
            history.splice(0, removeCount);
        }
    }

    /**
     * Get last price from history
     */
    _priceHistoryLast(symbol) {
        const history = this.priceHistory.get(symbol) || [];
        if (history.length < 2) return null;
        return history[history.length - 2]?.price;  // Second to last (before current)
    }

    /**
     * Perform price monitoring checks and cancel if needed
     * @param {string} symbol - Trading symbol
     * @param {number} midPrice - Current mid price
     * @returns {Promise<boolean>} - True if orders were cancelled
     */
    async _checkPriceAndCancelIfNeeded(symbol, midPrice) {
        const timestamp = Date.now();
        let cancelled = false;

        // Collect all at-risk sides from both checks
        const distanceSides = this._checkPriceDistance(symbol, midPrice);
        const velocitySides = this._checkPriceVelocity(symbol, midPrice, timestamp);

        // Merge all at-risk sides (union)
        const allAtRisk = new Set([...distanceSides, ...velocitySides]);

        // Cancel all at-risk sides
        for (const side of allAtRisk) {
            const reason = distanceSides.has(side) ? 'distance_protection' : 'velocity_protection';
            await this.handleCancel({
                type: 'cancel',
                symbol,
                side: side,
                reason: reason
            });
            cancelled = true;
        }

        return cancelled;
    }

    /**
     * Get available budget for a side
     * @param {string} symbol - Trading symbol
     * @param {string} side - 'buy' or 'sell'
     * @returns {number} - Available budget in USD
     */
    _getAvailableBudget(symbol, side) {
        const sideTarget = (this.clientConfig?.positionValue || 1000) / 2;  // Half of total per side

        // Get current position USD from budget state
        let budget = this.budgetState.get(symbol);
        if (!budget) {
            budget = {
                buy: { target: sideTarget, order: 0, position: 0 },
                sell: { target: sideTarget, order: 0, position: 0 }
            };
            this.budgetState.set(symbol, budget);
        }

        // Calculate outstanding order USD dynamically from ordersByLevel
        // (more reliable than incremental tracking which can drift)
        let orderUsd = 0;
        const levelMap = this.ordersByLevel.get(symbol);
        if (levelMap) {
            for (const [levelKey, orderInfo] of levelMap) {
                const orderSide = levelKey.startsWith('buy') ? 'buy' : 'sell';
                if (orderSide === side && orderInfo.price && orderInfo.size) {
                    orderUsd += orderInfo.price * orderInfo.size;
                }
            }
        }

        const sideData = budget[side];
        const available = sideData.target - orderUsd - sideData.position;
        return Math.max(0, available);
    }

    /**
     * Scale order size based on client's position value vs server's default
     * @param {number} serverSizeUsd - Size from server signal
     * @param {string} symbol - Trading symbol (unused but available for per-symbol scaling)
     * @returns {number} - Scaled size in USD
     */
    _scaleOrderSize(serverSizeUsd, symbol) {
        // Server assumes base position value of $1000 (from plan)
        // Scale based on client's configured position value
        const serverPositionValue = 1000;  // Default assumption
        const clientPositionValue = this.clientConfig.positionValue;
        const scale = clientPositionValue / serverPositionValue;
        return serverSizeUsd * scale;
    }

    // NOTE: _updateBudgetAfterPlace was removed - order USD is now calculated
    // dynamically from ordersByLevel in _getAvailableBudget() to avoid drift

    /**
     * Update budget when an order fills (called from fill handler)
     * @param {string} symbol - Trading symbol
     * @param {string} side - 'buy' or 'sell'
     * @param {number} filledSizeUsd - Filled size in USD
     */
    _updateBudgetOnFill(symbol, side, filledSizeUsd) {
        let budget = this.budgetState.get(symbol);
        if (!budget) return;

        // Add to position budget (order USD is calculated dynamically from ordersByLevel)
        budget[side].position += filledSizeUsd;

        this.log(`Budget update on fill: ${symbol} ${side} +$${filledSizeUsd.toFixed(0)} position`);
    }

    /**
     * Reset budget for a symbol (e.g., when position closes)
     * @param {string} symbol - Trading symbol
     * @returns {Object} - The new budget object
     */
    _resetBudget(symbol) {
        const sideTarget = this.clientConfig.positionValue / 2;
        const newBudget = {
            buy: { target: sideTarget, order: 0, position: 0 },
            sell: { target: sideTarget, order: 0, position: 0 }
        };
        this.budgetState.set(symbol, newBudget);
        this.log(`Budget reset for ${symbol}`);
        return newBudget;
    }

    /**
     * Get market data for a symbol (for UI display)
     * @param {string} symbol - Trading symbol
     * @returns {Object|null} - Market data or null
     */
    getMarketData(symbol) {
        return this.marketData.get(symbol) || null;
    }

    /**
     * Get all market data (for UI display)
     * @returns {Object} - Map of symbol → market data
     */
    getAllMarketData() {
        const result = {};
        for (const [symbol, data] of this.marketData) {
            result[symbol] = data;
        }
        return result;
    }

    /**
     * Get inventory state for a symbol
     * @param {string} symbol - Trading symbol
     * @returns {Object|null} - Inventory state or null
     */
    getInventoryState(symbol) {
        return this.inventoryState.get(symbol) || null;
    }

    /**
     * Get budget state for a symbol
     * @param {string} symbol - Trading symbol
     * @returns {Object|null} - Budget state or null
     */
    getBudgetState(symbol) {
        return this.budgetState.get(symbol) || null;
    }

    /**
     * Get handler statistics
     * @returns {Object} - Stats including counts and pending SL/TP
     */
    getStats() {
        // Count tracked orders per symbol
        let trackedOrdersCount = 0;
        const trackedBySymbol = {};
        for (const [symbol, orders] of this.trackedOrders) {
            trackedBySymbol[symbol] = orders.length;
            trackedOrdersCount += orders.length;
        }

        // Exit algo status
        const exitAlgoActive = {};
        for (const [symbol, state] of this.exitAlgoState) {
            if (state.active) {
                exitAlgoActive[symbol] = {
                    phase: state.phase,
                    breakoutDirection: state.breakoutDirection,
                    iteration: state.iteration,
                    elapsed: Math.floor((Date.now() - state.haltTime) / 1000)
                };
            }
        }

        // Fill pause status
        const fillPauseActive = {};
        const now = Date.now();
        for (const [symbol, until] of this.fillPauseUntil) {
            if (until > now) {
                fillPauseActive[symbol] = {
                    remainingSec: ((until - now) / 1000).toFixed(1),
                    until: new Date(until).toISOString()
                };
            }
        }

        return {
            ...this.stats,
            activeOrders: this.activeOrders.size,
            pendingSLTP: this.pendingSLTP.size,
            filledPositions: this.filledPositions.size,
            trackedOrders: trackedOrdersCount,
            trackedBySymbol,
            managedCoins: Array.from(this.managedCoins),
            enabled: this.enabled,
            exitAlgoPreset: this.exitPreset,
            exitAlgoActive: Object.keys(exitAlgoActive).length > 0 ? exitAlgoActive : null,
            fillPauseActive: Object.keys(fillPauseActive).length > 0 ? fillPauseActive : null
        };
    }

    /**
     * Get list of managed coins being traded (like Python's managed_coins)
     * Used by fill subscription to know which symbols to track
     */
    getManagedCoins() {
        return Array.from(this.managedCoins);
    }

    /**
     * Get unique DEXs from managed coins
     * e.g., ['xyz:NVDA', 'vntl:SPACEX'] → ['xyz', 'vntl']
     */
    getManagedDexs() {
        const dexs = new Set();
        for (const coin of this.managedCoins) {
            if (coin.includes(':')) {
                dexs.add(coin.split(':')[0]);
            } else {
                dexs.add('');  // main DEX
            }
        }
        return Array.from(dexs);
    }

    // ==================== EV+ EXIT ALGO IMPLEMENTATION ====================

    /**
     * Start the EV+ Exit Algo for a symbol
     * Called when inventory halt is triggered
     * @param {string} symbol - Trading symbol
     * @param {Object} inventoryCheck - { halted, ratio, coins, maxCoins }
     */
    async _startExitAlgo(symbol, inventoryCheck) {
        // Check if already active - set placeholder immediately to prevent race
        const existingState = this.exitAlgoState.get(symbol);
        if (existingState?.active) {
            this.log(`Exit algo already active for ${symbol} (phase: ${existingState.phase || 'unknown'})`);
            return;
        }

        // Set placeholder state immediately to prevent duplicate starts
        this.exitAlgoState.set(symbol, { active: true, phase: 'initializing', startTime: Date.now() });
        this.log(`${symbol} Exit algo: Initializing...`);

        // FORCE FRESH POSITION from exchange before starting
        // This prevents stale cached position direction causing "Position FLIPPED" errors
        this.log(`${symbol} Exit algo: Fetching fresh position from exchange...`);
        try {
            await this._forcePositionRefresh(symbol);
        } catch (e) {
            this.warn(`${symbol} Exit algo: Position refresh failed: ${e.message} - using cached data`);
        }

        // Get position info (now fresh from exchange)
        let position = null;
        try {
            if (window.BrowserPositionTracker) {
                position = window.BrowserPositionTracker.positions.get(symbol);
            }
        } catch (e) {
            this.error(`Failed to get position for exit algo: ${e.message}`);
            this.exitAlgoState.delete(symbol);  // Cleanup placeholder
            return;
        }

        if (!position || !position.size || Math.abs(position.size) < 0.0001) {
            this.log(`No position to exit for ${symbol} - clearing exit algo state`);
            this.exitAlgoState.delete(symbol);  // Cleanup placeholder
            return;
        }

        // Get entry price from position FIRST (needed for ATR sanity check)
        const entryPrice = parseFloat(position.entryPx || position.entryPrice || 0);
        if (!entryPrice || entryPrice <= 0) {
            this.error(`Exit algo: Invalid entry price for ${symbol}`);
            this.exitAlgoState.delete(symbol);  // Cleanup placeholder
            return;
        }

        // Get ATR (required for exit algo) - wait briefly if not available
        let atr = this.atrCache.get(symbol);
        if (!atr || atr <= 0) {
            // Wait briefly for ATR data (may come in next market data signal)
            this.log(`${symbol} Exit algo: Waiting for ATR data...`);
            await this._sleep(2000);
            atr = this.atrCache.get(symbol);
            if (!atr || atr <= 0) {
                this.error(`Exit algo: ATR unavailable for ${symbol} after wait, cannot proceed`);
                this.exitAlgoState.delete(symbol);  // Cleanup placeholder
                return;
            }
        }

        const atrPercent = (atr / entryPrice) * 100;
        this.log(`${symbol} Exit algo: ATR=$${atr.toFixed(6)} (${atrPercent.toFixed(4)}% of price)`);

        const isLong = position.size > 0;
        const exitSide = isLong ? 'sell' : 'buy';
        const positionSize = Math.abs(position.size);

        // Select preset
        const preset = this.exitPreset === 'AUTO' ? 'DEFAULT' : this.exitPreset;
        const exitParams = EVPLUS_EXIT_PRESETS[preset] || EVPLUS_EXIT_PRESETS['DEFAULT'];

        // Calculate initial range for logging
        const rangeUpper = entryPrice + (exitParams.range_base * atr);
        const rangeLower = entryPrice - (exitParams.range_base * atr);

        // Initialize exit state
        const exitState = {
            active: true,
            phase: 'range_wait',
            symbol,
            entryPrice,
            atr,
            isLong,
            exitSide,
            positionSize,
            preset,
            exitParams,
            haltTime: Date.now(),
            breakoutDirection: null,
            breakoutTime: null,
            iteration: 0,
            activeOrderOid: null,
            maxDuration: 30 * 60 * 1000  // 30 minute max duration
        };

        this.exitAlgoState.set(symbol, exitState);

        this.log(`🚀 EV+ Exit Algo started for ${symbol}: ` +
            `${isLong ? 'LONG' : 'SHORT'} ${positionSize.toFixed(4)} @ $${entryPrice.toFixed(5)}`);
        this.log(`   ATR=$${atr.toFixed(5)} (${atrPercent.toFixed(3)}% of price), preset=${preset}`);
        this.log(`   Initial range: [$${rangeLower.toFixed(5)}, $${rangeUpper.toFixed(5)}] (${exitParams.range_base}x ATR)`);

        // Run the exit algo loop (non-blocking)
        this._runExitAlgoLoop(symbol).catch(err => {
            this.error(`Exit algo FATAL error for ${symbol}: ${err.message}`);
            this.error(`Exit algo stack: ${err.stack || 'no stack'}`);
            // Ensure cleanup even on error - cancel any active order
            const state = this.exitAlgoState.get(symbol);
            if (state?.activeOrderOid) {
                this.hlClient.cancelOrder({ orderId: state.activeOrderOid, asset: symbol })
                    .catch(e => this.warn(`Failed to cancel exit order on error: ${e.message}`));
            }
            this._stopExitAlgo(symbol, false);
        });
    }

    /**
     * Main exit algo loop - waits for breakout then executes exit
     * @param {string} symbol - Trading symbol
     */
    async _runExitAlgoLoop(symbol) {
        const state = this.exitAlgoState.get(symbol);
        if (!state?.active) {
            this.warn(`${symbol} Exit algo loop: State not active, aborting`);
            return;
        }

        // Validate state has required fields
        if (!state.exitParams || !state.entryPrice || !state.atr) {
            this.error(`${symbol} Exit algo loop: Invalid state - missing required fields`);
            this.error(`   exitParams: ${!!state.exitParams}, entryPrice: ${state.entryPrice}, atr: ${state.atr}`);
            this._stopExitAlgo(symbol, false);
            return;
        }

        const { exitParams, entryPrice, isLong, exitSide } = state;
        // Use live ATR from cache if available and valid (prefer fresh data)
        let atr = state.atr;

        // ===== PHASE 1: Wait for range breakout =====
        this.log(`${symbol} Exit algo: PHASE 1 - Waiting for range breakout (${isLong ? 'LONG' : 'SHORT'})`);

        let breakoutDirection = null;
        let rangeWaitCounter = 0;
        const maxDuration = state.maxDuration || 30 * 60 * 1000;  // 30 min default

        while (breakoutDirection === null && state.active) {
            rangeWaitCounter++;

            // Check max duration timeout
            const totalElapsed = Date.now() - state.haltTime;
            if (totalElapsed > maxDuration) {
                this.error(`${symbol} Exit algo: MAX DURATION reached (${Math.floor(totalElapsed / 60000)} min) - forcing stop`);
                this._stopExitAlgo(symbol, false);
                return;
            }

            // Check if position already closed
            const currentPosition = await this._getPositionSize(symbol);
            if (Math.abs(currentPosition) < 0.0001) {
                this.log(`${symbol} Exit algo: Position closed during range wait - SUCCESS`);
                this._stopExitAlgo(symbol, true);
                return;
            }

            // Get current mid price
            const midPrice = this.lastMidPrice.get(symbol);
            if (!midPrice || midPrice <= 0) {
                if (rangeWaitCounter % 10 === 0) {
                    this.warn(`${symbol} Exit algo: No mid price data (waiting ${rangeWaitCounter}s)`);
                }
                await this._sleep(1000);
                continue;
            }

            // Calculate effective range with time decay
            const elapsed = (Date.now() - state.haltTime) / 1000;
            let effectiveRangeMult;
            if (elapsed < exitParams.range_decay_interval) {
                effectiveRangeMult = exitParams.range_base;
            } else {
                const decayPeriods = Math.floor((elapsed - exitParams.range_decay_interval) / exitParams.range_decay_interval);
                effectiveRangeMult = Math.max(exitParams.range_min, exitParams.range_base - (decayPeriods * exitParams.range_decay_rate));
            }

            const rangeUpper = entryPrice + (effectiveRangeMult * atr);
            const rangeLower = entryPrice - (effectiveRangeMult * atr);

            // Check for breakout
            if (isLong) {
                if (midPrice > rangeUpper) {
                    breakoutDirection = 'infavor';
                    state.breakoutTime = Date.now();
                    this.log(`${symbol} Exit algo: ✅ IN-FAVOR breakout! (mid=$${midPrice.toFixed(5)} > upper=$${rangeUpper.toFixed(5)})`);
                } else if (midPrice < rangeLower) {
                    breakoutDirection = 'unfavor';
                    state.breakoutTime = Date.now();
                    this.warn(`${symbol} Exit algo: ❌ UNFAVOR breakout! (mid=$${midPrice.toFixed(5)} < lower=$${rangeLower.toFixed(5)})`);
                }
            } else { // short
                if (midPrice < rangeLower) {
                    breakoutDirection = 'infavor';
                    state.breakoutTime = Date.now();
                    this.log(`${symbol} Exit algo: ✅ IN-FAVOR breakout! (mid=$${midPrice.toFixed(5)} < lower=$${rangeLower.toFixed(5)})`);
                } else if (midPrice > rangeUpper) {
                    breakoutDirection = 'unfavor';
                    state.breakoutTime = Date.now();
                    this.warn(`${symbol} Exit algo: ❌ UNFAVOR breakout! (mid=$${midPrice.toFixed(5)} > upper=$${rangeUpper.toFixed(5)})`);
                }
            }

            if (breakoutDirection === null) {
                // Log every 10 seconds (more frequent than before)
                if (rangeWaitCounter % 10 === 0) {
                    this.log(`${symbol} Exit algo P1: mid=$${midPrice.toFixed(5)}, entry=$${entryPrice.toFixed(5)}, ` +
                        `range=[$${rangeLower.toFixed(5)}, $${rangeUpper.toFixed(5)}] (${effectiveRangeMult.toFixed(2)}x ATR=$${atr.toFixed(5)}), ` +
                        `elapsed=${elapsed.toFixed(0)}s`);
                }
                await this._sleep(1000);
            }
        }

        if (!state.active) {
            this.log(`${symbol} Exit algo: State became inactive during range wait`);
            return;
        }

        state.breakoutDirection = breakoutDirection;
        state.phase = breakoutDirection === 'infavor' ? 'infavor_exit' : 'unfavor_exit';

        // ===== PHASE 2/3: Execute exit based on breakout direction =====
        this.log(`${symbol} Exit algo: Entering ${state.phase} phase`);

        let success;
        if (breakoutDirection === 'infavor') {
            success = await this._runInfavorExit(symbol);
        } else {
            success = await this._runUnfavorExit(symbol);
        }

        this._stopExitAlgo(symbol, success);
    }

    /**
     * IN-FAVOR exit: Price moved in our direction. Use relaxed maker orders.
     * @param {string} symbol - Trading symbol
     * @returns {boolean} - Success
     */
    async _runInfavorExit(symbol) {
        const state = this.exitAlgoState.get(symbol);
        if (!state?.active) return false;

        const { exitParams, atr, exitSide, breakoutTime } = state;
        const baseMultiple = 1.0;
        const maxIterations = 3600;

        this.log(`${symbol} Exit algo: Phase 2 - IN-FAVOR exit (relaxed maker)`);

        while (state.active && state.iteration < maxIterations) {
            state.iteration++;

            // Check position
            const currentSize = await this._getPositionSize(symbol);
            if (Math.abs(currentSize) < 0.0001) {
                this.log(`${symbol} IN-FAVOR exit: Position closed!`);
                return true;
            }

            // Check for position flip (use percentage threshold)
            const currentIsLong = currentSize > 0;
            const currentExitSide = currentIsLong ? 'sell' : 'buy';
            const minFlipThreshold = Math.max(0.0001, state.positionSize * 0.01);  // 1% of original
            if (currentExitSide !== exitSide && Math.abs(currentSize) > minFlipThreshold) {
                this.warn(`${symbol} IN-FAVOR exit: Position FLIPPED! Aborting.`);
                return false;
            }

            const midPrice = this.lastMidPrice.get(symbol);
            if (!midPrice || midPrice <= 0) {
                await this._sleep(1000);
                continue;
            }

            // Calculate effective multiple with delayed decay
            const elapsed = (Date.now() - breakoutTime) / 1000;
            let effectiveMultiple;
            if (elapsed < exitParams.infavor_wait) {
                effectiveMultiple = baseMultiple;
            } else {
                // Use infavor_decay_interval parameter (was hardcoded to 60)
                const decayInterval = exitParams.infavor_decay_interval || 60;
                const decayPeriods = Math.floor((elapsed - exitParams.infavor_wait) / decayInterval);
                effectiveMultiple = Math.max(exitParams.infavor_min, baseMultiple - (decayPeriods * exitParams.infavor_decay_rate));
            }

            // Calculate exit price (on profit side of mid)
            let exitPrice;
            if (exitSide === 'sell') {
                exitPrice = midPrice + (effectiveMultiple * atr);
            } else {
                exitPrice = midPrice - (effectiveMultiple * atr);
            }

            this.log(`${symbol} IN-FAVOR exit: iter=${state.iteration}, mid=$${midPrice.toFixed(5)}, ` +
                `mult=${effectiveMultiple.toFixed(2)}x, exit=$${exitPrice.toFixed(5)}, elapsed=${elapsed.toFixed(0)}s`);

            // Place exit order and wait
            const filled = await this._placeExitOrderAndWait(symbol, exitSide, exitPrice, Math.abs(currentSize), exitParams.order_validity);
            if (filled) {
                await this._exitAlgoPostFillCleanup(symbol, 'infavor');
                return true;
            }

            await this._sleep(1000);
        }

        return false;
    }

    /**
     * UNFAVOR exit: Price moved against us. Start as maker, transition to taker.
     * @param {string} symbol - Trading symbol
     * @returns {boolean} - Success
     */
    async _runUnfavorExit(symbol) {
        const state = this.exitAlgoState.get(symbol);
        if (!state?.active) return false;

        const { exitParams, atr, exitSide, breakoutTime } = state;
        const baseMultiple = 1.0;
        const maxIterations = 3600;

        this.log(`${symbol} Exit algo: Phase 3 - UNFAVOR exit (aggressive)`);

        while (state.active && state.iteration < maxIterations) {
            state.iteration++;

            // Check position
            const currentSize = await this._getPositionSize(symbol);
            if (Math.abs(currentSize) < 0.0001) {
                this.log(`${symbol} UNFAVOR exit: Position closed!`);
                return true;
            }

            // Check for position flip (use percentage threshold)
            const currentIsLong = currentSize > 0;
            const currentExitSide = currentIsLong ? 'sell' : 'buy';
            const minFlipThreshold = Math.max(0.0001, state.positionSize * 0.01);  // 1% of original
            if (currentExitSide !== exitSide && Math.abs(currentSize) > minFlipThreshold) {
                this.warn(`${symbol} UNFAVOR exit: Position FLIPPED! Aborting.`);
                return false;
            }

            const midPrice = this.lastMidPrice.get(symbol);
            if (!midPrice || midPrice <= 0) {
                await this._sleep(1000);
                continue;
            }

            // Calculate effective multiple with immediate decay
            const elapsed = (Date.now() - breakoutTime) / 1000;
            const decayPeriods = Math.floor(elapsed / exitParams.unfavor_decay_interval);
            const effectiveMultiple = Math.max(exitParams.unfavor_min, baseMultiple - (decayPeriods * exitParams.unfavor_decay_rate));

            const isTaker = effectiveMultiple < 0;
            const status = isTaker ? 'TAKER' : 'MAKER';

            // Calculate exit price
            let exitPrice;
            if (exitSide === 'sell') {
                exitPrice = midPrice + (effectiveMultiple * atr);
            } else {
                exitPrice = midPrice - (effectiveMultiple * atr);
            }

            this.warn(`${symbol} UNFAVOR exit [${status}]: iter=${state.iteration}, mid=$${midPrice.toFixed(5)}, ` +
                `mult=${effectiveMultiple.toFixed(2)}x, exit=$${exitPrice.toFixed(5)}, elapsed=${elapsed.toFixed(0)}s`);

            // Place exit order and wait
            const filled = await this._placeExitOrderAndWait(symbol, exitSide, exitPrice, Math.abs(currentSize), exitParams.order_validity);
            if (filled) {
                await this._exitAlgoPostFillCleanup(symbol, 'unfavor');
                return true;
            }

            await this._sleep(1000);
        }

        return false;
    }

    /**
     * Place an exit order and wait for fill
     * @param {string} symbol - Trading symbol
     * @param {string} side - 'buy' or 'sell'
     * @param {number} price - Exit price
     * @param {number} size - Position size
     * @param {number} validitySec - How long to wait for fill
     * @returns {boolean} - True if filled
     */
    async _placeExitOrderAndWait(symbol, side, price, size, validitySec) {
        try {
            const state = this.exitAlgoState.get(symbol);
            if (!state) return false;

            // Cancel any existing exit order - clear OID first to prevent race
            if (state.activeOrderOid) {
                const oidToCancel = state.activeOrderOid;
                state.activeOrderOid = null;  // Clear first to prevent race
                try {
                    await this.hlClient.cancelOrder({ orderId: oidToCancel, asset: symbol });
                    // Also remove from trackedOrders to prevent stale detection
                    this._removeFromTrackedOrders(symbol, oidToCancel);
                } catch (e) {
                    this.warn(`Failed to cancel exit order ${oidToCancel}: ${e.message}`);
                    // Don't restore - order may have filled
                }
            }

            // Place new exit order
            const orders = [{
                asset: symbol,
                side: side,
                price: price,
                size: size,
                reduceOnly: true
            }];

            const result = await this.rateLimitedRequest(() => this.hlClient.batchPlace(orders));

            // Track order OID
            if (result?.response?.data?.statuses?.[0]?.resting?.oid) {
                state.activeOrderOid = result.response.data.statuses[0].resting.oid;

                // FIX: Also add to trackedOrders to prevent _syncOrderTracking from cancelling as "stale"
                if (!this.trackedOrders.has(symbol)) {
                    this.trackedOrders.set(symbol, []);
                }
                this.trackedOrders.get(symbol).push({
                    oid: state.activeOrderOid,
                    side: side,
                    price: price,
                    size: size,
                    timestamp: Date.now(),
                    isExitOrder: true  // Flag to identify exit orders
                });

                this.log(`${symbol} Exit order placed: ${side.toUpperCase()} ${size.toFixed(4)} @ $${price.toFixed(5)} (oid=${state.activeOrderOid})`);
            } else if (result?.response?.data?.statuses?.[0]?.filled) {
                // Filled immediately
                this.log(`${symbol} Exit order filled immediately!`);
                return true;
            }

            // Wait for fill
            const startTime = Date.now();
            while ((Date.now() - startTime) < validitySec * 1000) {
                const currentSize = await this._getPositionSize(symbol);
                if (Math.abs(currentSize) < 0.0001) {
                    return true;
                }
                await this._sleep(1000);
            }

            // Not filled - cancel for re-quote
            if (state.activeOrderOid) {
                const oidToCancel = state.activeOrderOid;
                try {
                    await this.hlClient.cancelOrder({ orderId: oidToCancel, asset: symbol });
                    // Also remove from trackedOrders
                    this._removeFromTrackedOrders(symbol, oidToCancel);
                } catch (e) {
                    // Ignore
                }
                state.activeOrderOid = null;
            }

            this.debug(`${symbol} Exit order not filled after ${validitySec}s, will re-quote`);
            return false;

        } catch (error) {
            this.error(`${symbol} Exit order error: ${error.message}`);

            // CRITICAL FIX: Detect "Reduce only order would increase position" error
            // This means position is 0 on exchange but position tracker has stale data
            if (error.message && error.message.includes('Reduce only order would increase position')) {
                this.warn(`${symbol} Exit algo: Exchange says no position - forcing position refresh`);

                // Force fresh position check from exchange API (bypass cache)
                try {
                    await this._forcePositionRefresh(symbol);
                    const freshPosition = await this._getPositionSize(symbol);
                    if (Math.abs(freshPosition) < 0.0001) {
                        this.log(`${symbol} Exit algo: Position confirmed closed after refresh - SUCCESS`);
                        return true;  // Position truly closed, exit algo will stop
                    }
                } catch (refreshError) {
                    this.warn(`${symbol} Exit algo: Position refresh failed: ${refreshError.message}`);
                }
            }

            return false;
        }
    }

    /**
     * Force a fresh position check from exchange API for a specific symbol
     * Called when we suspect position tracker has stale data
     * For HIP-3 symbols (xyz:NVDA), this queries the specific DEX
     */
    async _forcePositionRefresh(symbol) {
        if (!window.BrowserPositionTracker) return;

        // For HIP-3 symbols, extract DEX name and query it specifically
        const isHip3 = symbol.includes(':');
        if (isHip3) {
            const dexName = symbol.split(':')[0];  // xyz from xyz:NVDA
            this.debug(`${symbol} Forcing position refresh from ${dexName} DEX`);

            // Query the specific DEX for fresh position data
            try {
                const infoClient = window.BrowserPositionTracker.infoClient;
                if (!infoClient) {
                    this.warn(`${symbol} Cannot refresh - infoClient not available`);
                    await window.BrowserPositionTracker.pollData();  // Fallback
                    return;
                }

                const userState = await infoClient.clearinghouseState({
                    user: window.BrowserPositionTracker.publicAddress,
                    dex: dexName
                });

                // Update position tracker with fresh data for this symbol
                if (userState && userState.assetPositions) {
                    const positionData = userState.assetPositions.find(p => p.position?.coin === symbol);
                    if (positionData && Math.abs(parseFloat(positionData.position.szi)) > 0.0001) {
                        // Position still exists - update tracker
                        const pos = positionData.position;
                        window.BrowserPositionTracker.positions.set(symbol, {
                            coin: symbol,
                            size: parseFloat(pos.szi),
                            entryPrice: parseFloat(pos.entryPx || 0),
                            unrealizedPnl: parseFloat(pos.unrealizedPnl || 0),
                            side: parseFloat(pos.szi) > 0 ? 'LONG' : 'SHORT',
                            dex: dexName,
                            isHip3: true
                        });
                        this.debug(`${symbol} Position updated: ${pos.szi} coins`);
                    } else {
                        // Position is 0 - remove from tracker
                        window.BrowserPositionTracker.positions.delete(symbol);
                        this.log(`${symbol} Position is 0 on exchange - cleared from tracker`);
                    }
                } else {
                    // No position data returned - position is 0
                    window.BrowserPositionTracker.positions.delete(symbol);
                    this.log(`${symbol} No position data from ${dexName} DEX - cleared from tracker`);
                }
            } catch (error) {
                this.warn(`${symbol} Position refresh error: ${error.message}`);
                // Fallback to regular poll
                await window.BrowserPositionTracker.pollData();
            }
        } else {
            // Regular perp - use standard poll
            await window.BrowserPositionTracker.pollData();
            this.debug(`${symbol} Forced position refresh from exchange`);
        }
    }

    /**
     * Remove an order from trackedOrders by OID
     * @param {string} symbol - Trading symbol
     * @param {number} oid - Order ID to remove
     */
    _removeFromTrackedOrders(symbol, oid) {
        const tracked = this.trackedOrders.get(symbol);
        if (tracked) {
            const index = tracked.findIndex(o => String(o.oid) === String(oid));
            if (index !== -1) {
                tracked.splice(index, 1);
                this.debug(`${symbol} Removed OID ${oid} from trackedOrders`);
            }
        }

        // ALSO remove from ordersByLevel
        const levelMap = this.ordersByLevel.get(symbol);
        if (levelMap) {
            for (const [levelKey, orderInfo] of levelMap) {
                if (String(orderInfo.oid) === String(oid)) {
                    levelMap.delete(levelKey);
                    this.debug(`${symbol} Removed OID ${oid} from level ${levelKey}`);
                    break;
                }
            }
        }
    }

    /**
     * Post-fill cleanup after exit algo completes
     * @param {string} symbol - Trading symbol
     * @param {string} exitType - 'infavor' or 'unfavor'
     */
    async _exitAlgoPostFillCleanup(symbol, exitType) {
        this.log(`${symbol} Exit algo FILLED (${exitType}): Cancelling all remaining orders`);

        // Cancel all orders to prevent re-accumulation
        await this.handleCancel({ type: 'cancel', symbol, side: 'all', reason: `exit_${exitType}_filled` });

        // Reset budget
        this._resetBudget(symbol);
    }

    /**
     * Stop the exit algo for a symbol
     * @param {string} symbol - Trading symbol
     * @param {boolean} success - Whether exit was successful
     */
    _stopExitAlgo(symbol, success) {
        const state = this.exitAlgoState.get(symbol);
        if (!state) return;

        // Delete from map IMMEDIATELY to prevent race conditions
        // (another signal could start a new exit algo while we're cleaning up)
        this.exitAlgoState.delete(symbol);

        // Mark inactive for any running loops that may still reference this state
        state.active = false;

        // Cancel any active order (async, may fail if already cancelled)
        if (state.activeOrderOid) {
            this.hlClient.cancelOrder({ orderId: state.activeOrderOid, asset: symbol })
                .catch(e => this.warn(`Failed to cancel exit order on stop: ${e.message}`));
        }

        if (success) {
            this.log(`✅ ${symbol} Exit algo completed successfully`);
        } else {
            this.error(`❌ ${symbol} Exit algo failed - manual intervention may be required`);
        }
    }

    /**
     * Get current position size for a symbol
     * @param {string} symbol - Trading symbol
     * @returns {number} - Position size (positive=long, negative=short)
     */
    async _getPositionSize(symbol) {
        try {
            if (window.BrowserPositionTracker) {
                const position = window.BrowserPositionTracker.positions.get(symbol);
                return position?.size || 0;
            }
            return 0;
        } catch (e) {
            return 0;
        }
    }

    /**
     * Sleep helper
     * @param {number} ms - Milliseconds to sleep
     */
    _sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * Get exit algo state for a symbol (for UI/debugging)
     * @param {string} symbol - Trading symbol
     * @returns {Object|null} - Exit state or null
     */
    getExitAlgoState(symbol) {
        return this.exitAlgoState.get(symbol) || null;
    }

    /**
     * Check if exit algo is active for any symbol
     * @returns {boolean} - True if any exit algo is running
     */
    isExitAlgoActive() {
        for (const state of this.exitAlgoState.values()) {
            if (state.active) return true;
        }
        return false;
    }

    /**
     * Force stop exit algo for a symbol (emergency)
     * @param {string} symbol - Trading symbol
     */
    forceStopExitAlgo(symbol) {
        this._stopExitAlgo(symbol, false);
        this.warn(`${symbol} Exit algo force-stopped by user`);
    }

    /**
     * Set exit algo preset
     * @param {string} preset - 'AUTO', 'FAST', 'DEFAULT', or 'SLOW'
     */
    setExitPreset(preset) {
        const normalized = (preset || '').toUpperCase();
        const validPresets = ['AUTO', 'FAST', 'DEFAULT', 'SLOW'];
        if (validPresets.includes(normalized)) {
            this.exitPreset = normalized;
            this.log(`Exit algo preset set to: ${this.exitPreset}`);
        } else {
            this.warn(`Invalid exit preset: ${preset}, keeping current: ${this.exitPreset}`);
        }
    }

    /**
     * Enable/disable the handler
     * @param {boolean} enabled
     */
    setEnabled(enabled) {
        this.enabled = enabled;

        // Stop/start order sync based on enabled state
        if (enabled && !this.orderSyncInterval) {
            this._startOrderSync();
        } else if (!enabled && this.orderSyncInterval) {
            clearInterval(this.orderSyncInterval);
            this.orderSyncInterval = null;
            this.log('Order sync stopped');
        }

        this.log(`Handler ${enabled ? 'ENABLED' : 'DISABLED'}`);
    }

    /**
     * Clear all tracking data
     */
    reset() {
        this.activeOrders.clear();
        this.pendingSLTP.clear();
        this.filledPositions.clear();
        this.trackedOrders.clear();
        this.managedCoins.clear();
        this.inventoryState.clear();
        this.budgetState.clear();
        this.lastMidPrice.clear();
        this.marketData.clear();
        this.ordersCache = null;
        this.ordersCacheTime = 0;
        this.signalTimes = [];
        this.fillPauseUntil.clear();  // Clear fill pauses
        this.ordersByLevel.clear();   // Clear level-based tracking

        // Stop any active exit algos
        for (const [symbol, state] of this.exitAlgoState) {
            if (state.active) {
                this._stopExitAlgo(symbol, false);
            }
        }
        this.exitAlgoState.clear();
        this.atrCache.clear();
        this.positionEntries.clear();

        this.stats = {
            placed: 0,
            edited: 0,
            cancelled: 0,
            sltpPlaced: 0,
            errors: 0,
            rateLimitHits: 0,
            lastSignalTime: null,
            signalsPerMinute: 0
        };
        this.log('Handler RESET - all tracking data cleared');
    }

    /**
     * Get pending SL/TP orders (for debugging)
     * @returns {Array} - List of pending SL/TP data
     */
    getPendingSLTP() {
        return Array.from(this.pendingSLTP.entries()).map(([oid, data]) => ({
            oid,
            ...data
        }));
    }
}

// Export singleton instance
if (typeof window !== 'undefined') {
    window.MM4SignalHandler = new MM4SignalHandler();
}

export default MM4SignalHandler;
